import { Building2, Circle, Clock, Edit3, Eye, Mail, MapPin, PackagePlus, Phone, Save, Settings, Trash2, User, Users } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useMergeGroups, type MergeGroup } from '../../context/MergeGroupContext';
import { areaApi } from '../../services/areaApi';
import { dividerApi } from '../../services/dividerApi';
import { floorApi, type AreaDto, type DividerDto, type FloorResponseDto, type RestaurantTableRequestDto } from '../../services/floorApi';
import { reservationApi } from '../../services/reservationApi';
import { tableApi } from '../../services/tableApi';
import type { ReserveTableDTO, RestaurantTable } from '../../types/reservations';
import { getFloorPlanStatusColor } from '../../utils/tableStatusUtils';
import { useTableStatusSync } from '../../hooks/useTableStatusSync';
import { MergePreviewDialog } from '../dialogs/MergePreviewDialog';
import MergeHistoryDialog from '../dialogs/MergeHistoryDialog';
import TableDeleteConfirmationDialog from '../dialogs/TableDeleteConfirmationDialog';
import UpcomingReservationsCard from './UpcomingReservationsCard';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

// Type aliases for union types
type TableStatus = 'available' | 'occupied' | 'reserved' | 'maintenance';
type TableShape = 'rectangle' | 'circle';
type AreaType = 'dining' | 'bar' | 'kitchen' | 'storage' | 'restroom';
type DividerType = 'wall' | 'partition' | 'counter';
type FloorKey = 'floor1' | 'floor2' | 'rooftop';

interface CustomerInfo {
  name: string;
  email?: string;
  phone: string;
  guestNo: number;
  reservationTime?: string;
  specialRequests?: string;
}

interface Table {
  id: string;
  name: string;
  xposition: number;
  yposition: number;
  width: number;
  height: number;
  seats: number;
  status: TableStatus;
  shape: TableShape;
  rotation: number;
  description: string; // Description field for backend compatibility
  textSize?: number; // Font size multiplier (0.5 to 2.0, default 1.0)
  lastUpdated: Date;
  customer?: CustomerInfo;
}

interface FloorArea {
  id: string;
  name: string;
  xposition: number;
  yposition: number;
  width: number;
  height: number;
  type: AreaType;
  color: string;
  rotation?: number; // Rotation angle in degrees
}

interface Divider {
  id: string;
  x1position: number;
  y1position: number;
  x2position: number;
  y2position: number;
  thickness: number;
  color: string;
  type: DividerType;
  rotation: number; // Rotation angle in degrees
}

interface FloorPlan {
  id: string;
  name: string;
  tables: Table[];
  areas: FloorArea[];
  dividers: Divider[];
}

// Helper functions for converting backend data (extracted to avoid duplication)
const convertBackendTableToTable = (
  backendTable: RestaurantTableRequestDto,
  getTextSize: (id: string) => number
): Table => {
  const tableId = backendTable.id?.toString() || `table-${Date.now()}`;
  const normalizedShape: TableShape = String(backendTable.shape).toLowerCase() === 'circle' ? 'circle' : 'rectangle';
  
  // Convert backend status to table status (FloorApi uses 'AVAILABLE' format)
  let status: TableStatus = 'available';
  if (backendTable.status === 'AVAILABLE') status = 'available';
  else if (backendTable.status === 'OCCUPIED') status = 'occupied';
  else if (backendTable.status === 'RESERVED') status = 'reserved';
  else if (backendTable.status === 'MAINTENANCE') status = 'maintenance';
  
  return {
    id: tableId,
    name: backendTable.name || 'Unnamed Table',
    xposition: backendTable.xposition || 100,
    yposition: backendTable.yposition || 100,
    width: backendTable.width || 80,
    height: backendTable.height || 80,
    seats: backendTable.seats || 4,
    status,
    shape: normalizedShape,
    description: backendTable.description || `${normalizedShape.toLowerCase()} table`,
    rotation: backendTable.rotation || 0,
    textSize: getTextSize(tableId),
    lastUpdated: new Date()
  };
};

const convertBackendAreaToFloorArea = (area: AreaDto): FloorArea => ({
  id: area.id?.toString() || `area-${Date.now()}`,
  name: area.name || 'Unnamed Area',
  xposition: area.xposition || 0,
  yposition: area.yposition || 0,
  width: area.width || 200,
  height: area.height || 200,
  type: (area.type as AreaType) || 'dining',
  color: area.color || '#e0f7fa',
  rotation: 0 // Areas don't have rotation in backend
});

const convertBackendDividerToDivider = (divider: DividerDto): Divider => ({
  id: divider.id?.toString() || `divider-${Date.now()}`,
  x1position: divider.x1position || 0,
  y1position: divider.y1position || 0,
  x2position: divider.x2position || 100,
  y2position: divider.y2position || 0,
  thickness: divider.thickness || 4,
  color: divider.color || '#8b5cf6',
  type: (divider.type as DividerType) || 'wall',
  rotation: divider.rotation || 0
});

// Helper functions for floor conversions
const getFloorNumber = (floorKey: FloorKey): number => {
  if (floorKey === 'floor1') return 1;
  if (floorKey === 'floor2') return 2;
  return 3;
};

const getFloorString = (floorKey: FloorKey): string => {
  if (floorKey === 'floor1') return '1';
  if (floorKey === 'floor2') return '2';
  return '3';
};

const getFloorPrefix = (floorKey: FloorKey): string => {
  if (floorKey === 'floor1') return 'F1';
  if (floorKey === 'floor2') return 'F2';
  return 'RT';
};

const AdvancedFloorPlanEditor: React.FC = () => {
  const navigate = useNavigate();
  
  // Get merge groups from context
  const { mergeGroups, setMergeGroups, refreshMergeGroups, removeMergeGroup } = useMergeGroups();
  
  // Table merge functionality - simplified local implementation
  const [selectedTablesForMerge, setSelectedTablesForMerge] = useState<string[]>([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  
  // Merge Preview Dialog state
  const [showMergePreview, setShowMergePreview] = useState(false);
  
  // Merge History Dialog state
  const [showMergeHistory, setShowMergeHistory] = useState(false);
  
  // Delete Confirmation Dialog state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteTableInfo, setDeleteTableInfo] = useState<{
    id: string;
    name: string;
    hasReservations: boolean;
    reservationCount: number;
    reservationDetails: Array<{
      status: string;
      guestNo: number;
      date: string;
      time: string;
      username: string;
    }>;
  } | null>(null);
  
  // State management
  const [viewMode, setViewMode] = useState<'static' | 'edit'>('static');
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [selectedElementType, setSelectedElementType] = useState<'table' | 'area' | 'divider' | null>(null);
  const [showTableDetails, setShowTableDetails] = useState<string | null>(null);
  const [tableBookingDetails, setTableBookingDetails] = useState<Record<string, ReserveTableDTO | null>>({});
  const [tableReservations, setTableReservations] = useState<Record<string, ReserveTableDTO[]>>({});
  
  // Drag & Drop state
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ xposition: 0, yposition: 0 });
  
  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw' | 'start' | 'end' | null>(null);
  const [resizeStart, setResizeStart] = useState({ xposition: 0, yposition: 0, width: 0, height: 0 });
  
  // Canvas dimensions state - responsive
  const [canvasDimensions] = useState({ width: 1022, height: 538 });

  // Zoom state
  const [zoomLevel, setZoomLevel] = useState(1);
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 3;

  // Canvas pan state
  const [canvasPan, setCanvasPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Rotation state
  const [isRotating, setIsRotating] = useState(false);
  const [rotationStart, setRotationStart] = useState({ angle: 0, centerX: 0, centerY: 0 });
  
  // Animation frame for smooth updates
  const animationFrameRef = useRef<number | null>(null);
  
  // Backend sync state
  const [isSaving, setIsSaving] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const saveTimeoutRef = useRef<number | null>(null);
  const savingInProgressRef = useRef(false);
  
  // Ref to store refreshTableStatuses function for access in useCallback-based functions
  const refreshTableStatusesRef = useRef<((force?: boolean) => Promise<void>) | null>(null);

  // localStorage helpers for textSize persistence
  const getTextSizeFromLocalStorage = (tableId: string): number => {
    try {
      const stored = localStorage.getItem(`table-textsize-${tableId}`);
      return stored ? Number.parseFloat(stored) : 1;
    } catch {
      return 1;
    }
  };

  const setTextSizeToLocalStorage = (tableId: string, textSize: number) => {
    try {
      localStorage.setItem(`table-textsize-${tableId}`, textSize.toString());
    } catch (error) {
      toast.error(`Failed to delete merge group: ${error}`);
    }
  };

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Removed unused reservation-based status updater to reduce API calls and lint warnings

  // Load floor data from backend on component mount using new Floor API
  useEffect(() => {
    const loadFloorData = async () => {
      try {
        // Load all floor details at once using the new unified endpoint
        const [floor1Details, floor2Details, rooftopDetails] = await Promise.allSettled([
          floorApi.getFloorDetailsById(1),
          floorApi.getFloorDetailsById(2), 
          floorApi.getFloorDetailsById(3)
        ]);

        // Process each floor's data
        const processFloorData = (result: PromiseSettledResult<FloorResponseDto>, floorName: string) => {
          if (result.status === 'fulfilled') {
            const data = result.value;
           

            return {
              tables: data.tables?.map(t => convertBackendTableToTable(t, getTextSizeFromLocalStorage)) || [],
              areas: data.areas?.map(convertBackendAreaToFloorArea) || [],
              dividers: data.dividers?.map(convertBackendDividerToDivider) || []
            };
          } else {
            return {
              tables: [],
              areas: [],
              dividers: []
            };
          }
        };

        const floor1Data = processFloorData(floor1Details, 'Floor 1');
        const floor2Data = processFloorData(floor2Details, 'Floor 2');
        const rooftopData = processFloorData(rooftopDetails, 'Rooftop');

        // Update all floor plans with consolidated data
        const nextPlans: Record<FloorKey, FloorPlan> = {
          floor1: {
            id: 'floor1',
            name: '1st Floor',
            tables: floor1Data.tables,
            areas: floor1Data.areas,
            dividers: floor1Data.dividers
          },
          floor2: {
            id: 'floor2',
            name: '2nd Floor',
            tables: floor2Data.tables,
            areas: floor2Data.areas,
            dividers: floor2Data.dividers
          },
          rooftop: {
            id: 'rooftop',
            name: 'Roof Top',
            tables: rooftopData.tables,
            areas: rooftopData.areas,
            dividers: rooftopData.dividers
          }
        };

        setFloorPlans(nextPlans);
        // Initialize baseline snapshot for diffing to minimize future API calls
        baselineRef.current = structuredClone(nextPlans);

        const totalTables = floor1Data.tables.length + floor2Data.tables.length + rooftopData.tables.length;
        const totalAreas = floor1Data.areas.length + floor2Data.areas.length + rooftopData.areas.length;
        const totalDividers = floor1Data.dividers.length + floor2Data.dividers.length + rooftopData.dividers.length;

        toast.success(`Loaded ${totalTables} tables, ${totalAreas} areas, ${totalDividers} dividers from backend`);
        
      } catch (error) {
        //console.error('❌ Failed to load floor data from backend:', error);
        toast.error('Failed to load floor data. Using fallback method...');
        
        // Fallback to the old method if the new API fails
        try {
          const allTables = await tableApi.getAllTablesFlat();
          
          // Convert and filter tables by floor
          const convertedTables: Table[] = allTables
            .filter(t => t.id && t.id > 0)
            .map(backendTable => {
              const tableId = backendTable.id.toString();
              const normalizedShape: TableShape = backendTable.shape === 'circle' ? 'circle' : 'rectangle';
              return {
                id: tableId,
                name: backendTable.name,
                xposition: backendTable.xposition || 100,
                yposition: backendTable.yposition || 100,
                width: backendTable.width || 80,
                height: backendTable.height || 80,
                seats: backendTable.seats,
                status: 'available' as const,
                shape: normalizedShape,
                description: backendTable.description || `${normalizedShape.toLowerCase()} table`,
                rotation: backendTable.rotation || 0,
                textSize: getTextSizeFromLocalStorage(tableId),
                lastUpdated: new Date()
              };
            });

          const floor1Tables = convertedTables.filter(t => {
            const backendTable = allTables.find(bt => bt.id.toString() === t.id);
            return !backendTable?.floor || backendTable.floor === '1';
          });
          
          const floor2Tables = convertedTables.filter(t => {
            const backendTable = allTables.find(bt => bt.id.toString() === t.id);
            return backendTable?.floor === '2';
          });
          
          const rooftopTables = convertedTables.filter(t => {
            const backendTable = allTables.find(bt => bt.id.toString() === t.id);
            return backendTable?.floor === '3';
          });

          setFloorPlans(prev => ({
            ...prev,
            floor1: { ...prev.floor1, tables: floor1Tables },
            floor2: { ...prev.floor2, tables: floor2Tables },
            rooftop: { ...prev.rooftop, tables: rooftopTables }
          }));

          toast.success(`Fallback loaded ${convertedTables.length} tables`);
        } catch (fallbackError) {
          //console.error('❌ Fallback also failed:', fallbackError);
          toast.error('Failed to load any floor data');
        }
      }
    };

    loadFloorData();
  }, []);

  // Function to load individual floor data when floor tab is clicked
  const loadIndividualFloorData = useCallback(async (floorKey: 'floor1' | 'floor2' | 'rooftop') => {
    const floorIdMap = { floor1: 1, floor2: 2, rooftop: 3 };
    const floorId = floorIdMap[floorKey];
    
    //console.log(`🏢 Loading individual floor data for ${floorKey} (ID: ${floorId})...`);
    setLoadingFloors(prev => ({ ...prev, [floorKey]: true }));
    
    try {
      // Call individual floor endpoint
      const floorDetails = await floorApi.getFloorDetailsById(floorId);
      
      // Determine floor name
      let floorName = '1st Floor';
      if (floorKey === 'floor2') floorName = '2nd Floor';
      else if (floorKey === 'rooftop') floorName = 'Roof Top';

      // Update only the specific floor plan
      setFloorPlans(prev => ({
        ...prev,
        [floorKey]: {
          id: floorKey,
          name: floorDetails.name || floorName,
          tables: floorDetails.tables?.map(t => convertBackendTableToTable(t, getTextSizeFromLocalStorage)) || [],
          areas: floorDetails.areas?.map(convertBackendAreaToFloorArea) || [],
          dividers: floorDetails.dividers?.map(convertBackendDividerToDivider) || []
        }
      }));

      const totalItems = (floorDetails.tables?.length || 0) + (floorDetails.areas?.length || 0) + (floorDetails.dividers?.length || 0);
      //console.log(`✅ Loaded ${floorKey} data: ${floorDetails.tables?.length || 0} tables, ${floorDetails.areas?.length || 0} areas, ${floorDetails.dividers?.length || 0} dividers`);
      
      // Force refresh table statuses to apply reservation-based statuses (reserved/occupied)
      // This ensures merged tables show correct status (orange for reserved) instead of 'available'
      setTimeout(() => {
        if (refreshTableStatusesRef.current) {
          refreshTableStatusesRef.current(true);
          //console.log('🔄 Triggered table status refresh after floor load');
        }
      }, 100);
      
      if (totalItems > 0) {
        toast.success(`Refreshed ${floorKey.replace('floor', 'Floor ').replace('rooftop', 'Rooftop')} data`);
      }
      
    } catch (error) {
      //console.error(`❌ Failed to load ${floorKey} data:`, error);
      toast.error(`Failed to refresh ${floorKey.replace('floor', 'Floor ').replace('rooftop', 'Rooftop')} data`);
    } finally {
      setLoadingFloors(prev => ({ ...prev, [floorKey]: false }));
    }
  }, []);



  // Merge groups are loaded from MergeGroupContext and refreshed when floor changes
  // See the useEffect with [currentFloor, refreshMergeGroups] dependency below

  // Multi-floor state
  const [currentFloor, setCurrentFloor] = useState<'floor1' | 'floor2' | 'rooftop'>('floor1');
  const [loadingFloors, setLoadingFloors] = useState<Record<string, boolean>>({});
  const [floorPlans, setFloorPlans] = useState<Record<'floor1' | 'floor2' | 'rooftop', FloorPlan>>({
    floor1: {
      id: 'floor1',
      name: '1st Floor',
      tables: [],
      areas: [],
      dividers: []
    },
    floor2: {
      id: 'floor2',
      name: '2nd Floor',
      tables: [],
      areas: [],
      dividers: []
    },
    rooftop: {
      id: 'rooftop',
      name: 'Roof Top',
      tables: [],
      areas: [],
      dividers: []
    }
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const currentFloorPlan = floorPlans[currentFloor];

  // Real-time table status sync via WebSocket
  const { tables: realTimeTableStatuses, refreshTableStatuses } = useTableStatusSync(0); // No polling, only WebSocket updates
  
  // Keep ref updated with latest refreshTableStatuses for use in useCallback closures
  useEffect(() => {
    refreshTableStatusesRef.current = refreshTableStatuses;
  }, [refreshTableStatuses]);

  // Helper function for shallow equality comparison
  const shallowEqual = <T,>(a: T, b: T) => JSON.stringify(a) === JSON.stringify(b);


  // Effect to sync real-time table statuses with floor plan displays
  // Runs when realTimeTableStatuses changes OR when currentFloor changes (to re-apply after floor load)
  useEffect(() => {
    if (!realTimeTableStatuses.length) return;

    // Create a lookup map for faster table status updates
    const statusMap = new Map(
      realTimeTableStatuses.map(table => [table.id?.toString() || '', table.status])
    );

    // Update floor plans with latest statuses
    setFloorPlans(prevPlans => {
      let hasChanges = false;
      const updatedPlans = { ...prevPlans };

      // Update each floor plan
      Object.keys(updatedPlans).forEach(floorKey => {
        const plan = updatedPlans[floorKey as FloorKey];
        const updatedTables = plan.tables.map(table => {
          const backendStatus = statusMap.get(table.id.toString());
          if (backendStatus) {
            // Convert backend status to table status (realTimeTableStatuses uses 'Available' format)
            let status: TableStatus = 'available';
            if (backendStatus === 'Available') status = 'available';
            else if (backendStatus === 'Occupied') status = 'occupied';
            else if (backendStatus === 'Reserved') status = 'reserved';
            else if (backendStatus === 'Maintenance') status = 'maintenance';
            
            // Only update if status has changed
            if (table.status !== status) {
              hasChanges = true;
              //console.log(`🎨 Updating table ${table.name} status: ${table.status} → ${status}`);
              return { ...table, status };
            }
          }
          return table;
        });

        // Always update the floor plan with the processed tables if any status changed
        if (hasChanges || floorKey === currentFloor) {
          updatedPlans[floorKey as FloorKey] = {
            ...plan,
            tables: updatedTables
          };
        }
      });

      return hasChanges ? updatedPlans : prevPlans;
    });
  }, [realTimeTableStatuses, currentFloor]); // Added currentFloor to re-apply statuses after floor switch


  // Effect to refresh merge groups when floor changes or on initial mount
  // This ensures merged table connections are displayed for the current floor
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const floorId = getFloorNumber(currentFloor);
    //console.log(`🔗 Refreshing merge groups for ${currentFloor} (floorId: ${floorId}) on date: ${today}`);
    refreshMergeGroups(today, floorId);
  }, [currentFloor, refreshMergeGroups]);


  // Main save function - saves all floor plan data to backend
  const saveToBackend = useCallback(async () => {
    // Debug: Log the call stack to see what's triggering the save
    //console.log('🚨 saveToBackend called! Stack trace:', new Error('Stack trace').stack);
    
    // Prevent duplicate saves
    if (savingInProgressRef.current) {
      //console.log('⏸️ Save already in progress, skipping...');
      return;
    }

    try {
      savingInProgressRef.current = true;
      setIsSaving(true);
      const floorNumber = getFloorNumber(currentFloor);

      // Separate new tables (temp IDs) from existing tables (numeric IDs)
      const newTables = currentFloorPlan.tables.filter(table => {
        const id = table.id.toString();
        return id.startsWith('new-') || id.startsWith('temp-') || id.startsWith('t');
      });

      const existingTables = currentFloorPlan.tables.filter(table => {
        const numericId = Number.parseInt(table.id.toString());
        return !Number.isNaN(numericId) && numericId > 0;
      });

      let createdCount = 0;
      let updatedCount = 0;

      // CREATE new tables - with floor-specific sequential naming
      if (newTables.length > 0) {
        //console.log(`Creating ${newTables.length} new table(s)...`);
        
        // Get all existing tables from backend to determine next sequential number
        let allExistingTables: Array<{name: string; floor: string}> = [];
        try {
          const allTables = await tableApi.getAllTablesFlat();
          allExistingTables = allTables.map(t => ({ name: t.name, floor: t.floor || '' }));
        } catch (error) {
          //console.warn('Failed to fetch existing tables, will use local state:', error);
          allExistingTables = currentFloorPlan.tables
            .filter(t => !t.id.toString().startsWith('t'))
            .map(t => ({ name: t.name, floor: currentFloor === 'floor1' ? '1' : currentFloor === 'floor2' ? '2' : '3' }));
        }
        
        // Calculate next sequential number for each floor type
        // Floor 1 & 2 share sequence, Rooftop (3) is independent
        const floor1And2Tables = allExistingTables.filter(t => t.floor === '1' || t.floor === '2');
        const rooftopTables = allExistingTables.filter(t => t.floor === '3');
        
        // Extract highest number from table names like "T1", "T2", "T15"
        const getHighestTableNumber = (tables: Array<{name: string}>) => {
          const numbers = tables
            .map(t => {
              const match = /^T(\d+)$/.exec(t.name);
              return match ? Number.parseInt(match[1]) : 0;
            })
            .filter(n => n > 0);
          return numbers.length > 0 ? Math.max(...numbers) : 0;
        };
        
        let nextFloor12Number = getHighestTableNumber(floor1And2Tables) + 1;
        let nextRooftopNumber = getHighestTableNumber(rooftopTables) + 1;
        
        for (const table of newTables) {
          try {
            // Determine sequential name based on floor
            const isRooftop = currentFloor === 'rooftop';
            let sequentialName = isRooftop 
              ? `T${nextRooftopNumber}`
              : `T${nextFloor12Number}`;
            
            // Check if name already exists and increment until we find unique name
            let nameExists = true;
            let attempts = 0;
            const maxAttempts = 100; // Prevent infinite loop
            
            while (nameExists && attempts < maxAttempts) {
              nameExists = allExistingTables.some(t => t.name === sequentialName);
              if (nameExists) {
                if (isRooftop) {
                  nextRooftopNumber++;
                  sequentialName = `T${nextRooftopNumber}`;
                } else {
                  nextFloor12Number++;
                  sequentialName = `T${nextFloor12Number}`;
                }
                attempts++;
              }
            }
            
            // Increment for next iteration
            if (isRooftop) {
              nextRooftopNumber++;
            } else {
              nextFloor12Number++;
            }
            
            const getFloorString = () => {
              if (currentFloor === 'floor1') return '1';
              if (currentFloor === 'floor2') return '2';
              return '3';
            };

            const getStatusColor = (status: string) => {
              if (status === 'available') return '#22c55e';
              if (status === 'occupied') return '#ef4444';
              if (status === 'reserved') return '#f59e0b';
              return '#6b7280';
            };
            
            const tableRequest = {
              name: sequentialName,
              seats: table.seats,
              status: (table.status.charAt(0).toUpperCase() + table.status.slice(1)) as 'Available' | 'Occupied' | 'Reserved' | 'Maintenance',
              location: currentFloorPlan.name || 'Main Floor',
              description: `${table.shape.toLowerCase()} table`,
              shape: table.shape,
              color: getStatusColor(table.status),
              xposition: table.xposition,
              yposition: table.yposition,
              floor: getFloorString(),
              floorId: floorNumber,
              width: table.width,
              height: table.height,
              rotation: table.rotation
            };

            //console.log(`📝 Creating table "${sequentialName}" on floor ${tableRequest.floor}`);
            const createdTable = await tableApi.createTable(tableRequest);
            createdCount++;

            // Update local state with backend-assigned ID and sequential name
            setFloorPlans(prev => ({
              ...prev,
              [currentFloor]: {
                ...prev[currentFloor],
                tables: prev[currentFloor].tables.map(t =>
                  t.id === table.id
                    ? { ...t, id: createdTable.id.toString(), name: sequentialName }
                    : t
                )
              }
            }));
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            //console.error(`Failed to create table:`, errorMsg);
            // Continue with other tables instead of failing completely
          }
        }
      }

      // UPDATE existing tables - only if they have changes
      if (existingTables.length > 0) {
        // Detect changes vs baseline to avoid redundant updates
        const baseline = baselineRef.current?.[currentFloor]?.tables || [];
        const tablesToUpdate = existingTables
          .filter(table => {
            const base = baseline.find(t => t.id === table.id);
            if (!base) return true; // Not in baseline, treat as changed
            // Compare persisted fields only
            const comparable = ((t: Table) => ({
              name: t.name,
              seats: t.seats,
              status: t.status,
              xposition: t.xposition,
              yposition: t.yposition,
              width: t.width,
              height: t.height,
              rotation: t.rotation,
              shape: t.shape,
              description: t.description,
            }));
            return !shallowEqual(comparable(base), comparable(table));
          })
          .map(table => {
          const numericId = Number.parseInt(table.id.toString());

          const getFloorString = () => {
            if (currentFloor === 'floor1') return '1';
            if (currentFloor === 'floor2') return '2';
            return '3';
          };

          const getStatusColor = (status: string) => {
            if (status === 'available') return '#22c55e';
            if (status === 'occupied') return '#ef4444';
            if (status === 'reserved') return '#f59e0b';
            return '#6b7280';
          };
          
          return {
            id: numericId,
            name: table.name,
            seats: table.seats,
            status: (table.status.charAt(0).toUpperCase() + table.status.slice(1)) as 'Available' | 'Occupied' | 'Reserved' | 'Maintenance',
            location: currentFloorPlan.name || 'Main Floor',
            description: `${table.shape.toLowerCase()} table`,
            shape: table.shape,
            color: getStatusColor(table.status),
            xposition: table.xposition,
            yposition: table.yposition,
            floor: getFloorString(),
            floorId: floorNumber,
            width: table.width,
            height: table.height,
            rotation: table.rotation
          };
        });

        // Validate all IDs
        const validTables = tablesToUpdate.filter(t => {
          if (Number.isNaN(t.id) || t.id <= 0) {
            //console.error('Invalid table found:', t);
            return false;
          }
          return true;
        });

        if (validTables.length > 0) {
          //console.log(`Updating ${validTables.length} existing table(s)...`);
          try {
            await tableApi.updateTables(validTables);
            updatedCount = validTables.length;
          } catch (error) {
            //console.error('Failed to update tables:', error);
            toast.error('Failed to update some tables');
          }
        }
      }

      // Save areas and dividers using individual API calls
      let areasCount = 0;
      let dividersCount = 0;

      // Save/Update Areas
      try {
        //console.log(`💾 Saving ${currentFloorPlan.areas.length} areas for ${currentFloor}...`);
        const baselineAreas = baselineRef.current?.[currentFloor]?.areas || [];
        for (const area of currentFloorPlan.areas) {
          // Skip if unchanged
          const base = baselineAreas.find(a => a.id === area.id);
          if (base && shallowEqual({ ...base, rotation: undefined }, { ...area, rotation: undefined })) {
            //console.log(`⏭️ Skipping unchanged area: ${area.name}`);
            continue;
          }
          const areaRequest = {
            id: typeof area.id === 'string' && area.id.startsWith('area-') ? undefined : Number(area.id),
            name: area.name,
            xposition: area.xposition,
            yposition: area.yposition,
            width: area.width,
            height: area.height,
            type: area.type,
            color: area.color,
            floorId: floorNumber
          };

          // console.log(`📤 Saving area "${area.name}":`, {
          //   areaRequest,
          //   isNew: !areaRequest.id,
          //   originalId: area.id,
          //   floorId: floorNumber,
          //   floorKey: currentFloor
          // });

          try {
            if (areaRequest.id) {
              //console.log(`🔄 Updating area ID ${areaRequest.id}`);
              await areaApi.updateArea(areaRequest.id, areaRequest);
            } else {
              //console.log(`🆕 Creating new area "${area.name}" on floor ${floorNumber} (${currentFloor})`);
              //console.log(`📦 Request payload:`, JSON.stringify(areaRequest, null, 2));
              const result = await areaApi.saveArea(areaRequest);
              //console.log(`✅ Area save result:`, result);
              
              // After successful save, reload floor data to get the real ID
              // This ensures the UI syncs with database state
              //console.log(`🔄 Reloading floor data to sync area IDs...`);
            }
            areasCount++;
          } catch (areaError: unknown) {
            // console.error(`❌ AREA SAVE FAILED for "${area.name}":`, {
            //   error: areaError,
            //   errorMessage: areaError instanceof Error ? areaError.message : String(areaError),
            //   areaRequest,
            //   floorId: floorNumber,
            //   floorKey: currentFloor
            // });
            
            if (areaError instanceof Error && areaError.message.includes('already exists')) {
              //console.warn(`⚠️ Backend claims area "${area.name}" already exists on floor ${floorNumber}, searching all areas...`);
              try {
                const allAreas = await areaApi.getAllAreas();
                //console.log(`🔍 All areas in database:`, allAreas);
                //console.log(`🔍 Looking for area with name="${area.name}" and floorId=${floorNumber}`);
                
                const existingArea = allAreas.find(a => 
                  a.name === area.name && 
                  a.floorId === floorNumber
                );
                
                if (existingArea?.id) {
                  //console.log(`✅ Found existing area ID ${existingArea.id}, updating it...`);
                  await areaApi.updateArea(existingArea.id, areaRequest);
                  areasCount++;
                  //console.log(`✅ Successfully updated existing area`);
                } else {
                  //console.error(`❌ Could not find existing area "${area.name}" with floorId ${floorNumber}`);
                  //console.error(`Available areas on floor ${floorNumber}:`, allAreas.filter(a => a.floorId === floorNumber));
                  toast.error(`Backend error: Area "${area.name}" conflict on ${currentFloor}`);
                }
              } catch (findError) {
                //console.error(`Failed to resolve area conflict for "${area.name}":`, findError);
                toast.error(`Failed to resolve area conflict for "${area.name}"`);
              }
            } else {
              //console.error(`Unexpected error saving area "${area.name}":`, areaError);
              toast.error(`Failed to save area "${area.name}"`);
            }
          }
        }
        //if (areasCount > 0) console.log(`✅ ${areasCount} areas saved/updated`);
      } catch (error) {
        toast.error(`Failed to save areas: ${error}`);
      }

      // Save/Update Dividers
      try {
        //console.log(`💾 Saving ${currentFloorPlan.dividers.length} dividers for ${currentFloor}...`);
        const baselineDividers = baselineRef.current?.[currentFloor]?.dividers || [];
        for (const divider of currentFloorPlan.dividers) {
          const base = baselineDividers.find(d => d.id === divider.id);
          if (base && shallowEqual(base, divider)) {
           //console.log(`⏭️ Skipping unchanged divider: ${divider.id}`);
            continue;
          }
          const dividerRequest = {
            id: typeof divider.id === 'string' && divider.id.startsWith('divider-') ? undefined : Number(divider.id),
            x1position: divider.x1position,
            y1position: divider.y1position,
            x2position: divider.x2position,
            y2position: divider.y2position,
            thickness: divider.thickness,
            type: divider.type,
            color: divider.color,
            rotation: divider.rotation || 0,
            floorId: floorNumber
          };

          // console.log(`📤 Saving divider "${divider.id}":`, {
          //   dividerRequest,
          //   isNew: !dividerRequest.id,
          //   originalId: divider.id,
          //   positions: {
          //     x1: divider.x1position,
          //     y1: divider.y1position,
          //     x2: divider.x2position,
          //     y2: divider.y2position
          //   }
          // });

          try {
            if (dividerRequest.id) {
              //console.log(`🔄 Updating divider ID ${dividerRequest.id}`);
              await dividerApi.updateDivider(dividerRequest.id, dividerRequest);
            } else {
              //console.log(`🆕 Creating new divider`);
              const result = await dividerApi.saveDivider(dividerRequest);
             //console.log(`✅ Divider save result:`, result);
            }
            dividersCount++;
          } catch (dividerError: unknown) {
            toast.error(`Failed to save divider: ${dividerError}`);
          }
        }
        //if (dividersCount > 0) console.log(`✅ ${dividersCount} dividers saved/updated`);
      } catch (error) {
        toast.error(`Failed to save dividers: ${error}`);
      }

      // Show success message
      const messages = [];
      if (createdCount > 0) messages.push(`${createdCount} tables created`);
      if (updatedCount > 0) messages.push(`${updatedCount} tables updated`);
      if (areasCount > 0) messages.push(`${areasCount} areas`);
      if (dividersCount > 0) messages.push(`${dividersCount} dividers`);
      
      if (messages.length > 0) {
        toast.success(`✓ Tables saved: ${messages.join(', ')}`, { duration: 2000 });
      } else {
        toast.success('✓ Floor plan saved', { duration: 2000 });
      }

      setUnsavedChanges(false);
      
      // Refresh floor data to get backend IDs and prevent duplicates
      //console.log('🔄 Refreshing floor data after save to sync backend IDs...');
      await loadIndividualFloorData(currentFloor);
      
      // Update baseline after successful save and reload
      // Wait for React state to settle, then update baseline
      await new Promise(resolve => setTimeout(resolve, 100));
      setFloorPlans(currentPlans => {
        baselineRef.current = JSON.parse(JSON.stringify(currentPlans));
        //console.log('✅ Baseline updated after save with synced IDs');
        return currentPlans; // Return unchanged to avoid re-render
      });
    } catch (error) {
      //console.error('Failed to save to backend:', error);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
      savingInProgressRef.current = false;
    }
  }, [currentFloorPlan, currentFloor, loadIndividualFloorData]);

  // Save floor plan function
  const saveFloorPlan = useCallback(async () => {
    if (!unsavedChanges || isSaving) {
      toast(unsavedChanges ? 'Save in progress...' : 'No changes to save');
      return;
    }
    await saveToBackend();
  }, [unsavedChanges, isSaving, saveToBackend]);

  // Modified floor switching function with individual API calls
  const switchToFloor = useCallback(async (floorKey: 'floor1' | 'floor2' | 'rooftop') => {
    // Check for unsaved changes before switching floors
    if (unsavedChanges && viewMode === 'edit') {
      const confirmSwitch = globalThis.confirm('You have unsaved changes. Do you want to save before switching floors?');
      if (confirmSwitch) {
        // User wants to save first - call saveToBackend directly
        await saveToBackend();
      } else {
        // User chose not to save, warn about losing changes
        const confirmLose = globalThis.confirm('Are you sure you want to lose your unsaved changes?');
        if (!confirmLose) {
          return; // Cancel floor switch
        }
        // Reset unsaved changes flag since user confirmed losing changes
        setUnsavedChanges(false);
      }
    }
    
    setCurrentFloor(floorKey);
    // Always refresh floor data when switching to ensure latest information
    await loadIndividualFloorData(floorKey);
  }, [loadIndividualFloorData, unsavedChanges, viewMode, saveToBackend]);

  // Status color functions - using shared utility
  const getStatusColor = (status: Table['status']) => {
    return getFloorPlanStatusColor(status);
  };

  const getStatusBadgeVariant = (status: Table['status']) => {
    switch (status) {
      case 'available': return 'default' as const;
      case 'occupied': return 'destructive' as const;
      case 'reserved': return 'secondary' as const;
      case 'maintenance': return 'outline' as const;
      default: return 'outline' as const;
    }
  };

  // Canvas rendering with enhanced visuals
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Save context state for zoom and pan transformation
    ctx.save();
    
    // Apply zoom transformation from canvas center
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    ctx.translate(centerX, centerY);
    ctx.scale(zoomLevel, zoomLevel);
    ctx.translate(-centerX, -centerY);
    
    // Apply pan offset
    ctx.translate(canvasPan.x, canvasPan.y);

    // Draw areas with fixed colors
    currentFloorPlan.areas.forEach(area => {
      ctx.save();
      
      if (area.rotation) {
        ctx.translate(area.xposition + area.width / 2, area.yposition + area.height / 2);
        ctx.rotate((area.rotation * Math.PI) / 180);
        ctx.fillStyle = area.color;
        ctx.fillRect(-area.width / 2, -area.height / 2, area.width, area.height);
        
        ctx.strokeStyle = selectedElement === area.id ? '#8b5cf6' : '#d1d5db';
        ctx.lineWidth = selectedElement === area.id ? 3 : 2;
        ctx.strokeRect(-area.width / 2, -area.height / 2, area.width, area.height);
        
        // Area label
        ctx.fillStyle = '#374151';
        ctx.font = '12px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(area.name, 0, 0);
      } else {
        ctx.fillStyle = area.color;
        ctx.fillRect(area.xposition, area.yposition, area.width, area.height);
        
        ctx.strokeStyle = selectedElement === area.id ? '#8b5cf6' : '#d1d5db';
        ctx.lineWidth = selectedElement === area.id ? 3 : 2;
        ctx.strokeRect(area.xposition, area.yposition, area.width, area.height);
        
        // Area label
        ctx.fillStyle = '#374151';
        ctx.font = '12px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(
          area.name,
          area.xposition + area.width / 2,
          area.yposition + 20
        );
      }
      
      ctx.restore();
    });

    // Draw dividers
    currentFloorPlan.dividers.forEach(divider => {
      ctx.strokeStyle = divider.color;
      ctx.lineWidth = divider.thickness;
      ctx.beginPath();
      ctx.moveTo(divider.x1position, divider.y1position);
      ctx.lineTo(divider.x2position, divider.y2position);
      ctx.stroke();
    });

    // Draw tables with default maintenance color, changing based on status
    currentFloorPlan.tables.forEach(table => {
      const color = getStatusColor(table.status);
      const isSelected = selectedElement === table.id;
      const isSelectedForMerge = selectedTablesForMerge.includes(table.id);
      
      // Check if table is in a merge group
      const mergeGroup = mergeGroups.find(group => group.tableIds.includes(table.id));
      const isInMergeGroup = !!mergeGroup;
      
      ctx.save();
      ctx.translate(table.xposition + table.width / 2, table.yposition + table.height / 2);
      ctx.rotate((table.rotation * Math.PI) / 180);
      
      ctx.fillStyle = color;
      // Purple border for edit mode selection, blue border for merge selection, thick colored border for merged tables
      if (isSelected) {
        ctx.strokeStyle = '#8b5cf6'; // Purple for edit mode
        ctx.lineWidth = 3;
      } else if (isSelectedForMerge) {
        ctx.strokeStyle = '#3b82f6'; // Blue for merge selection
        ctx.lineWidth = 3;
      } else if (isInMergeGroup) {
        ctx.strokeStyle = mergeGroup.color || '#10B981'; // Use group color or default green
        ctx.lineWidth = 4; // Thicker border for merged tables
      } else {
        ctx.strokeStyle = '#374151';
        ctx.lineWidth = 1;
      }
      
      if (table.shape === 'circle') {
        const radius = Math.min(table.width, table.height) / 2;
        ctx.beginPath();
        ctx.arc(0, 0, radius, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      } else {
        ctx.fillRect(-table.width / 2, -table.height / 2, table.width, table.height);
        ctx.strokeRect(-table.width / 2, -table.height / 2, table.width, table.height);
      }
      
      // Enhanced table information display with dynamic font scaling
      ctx.fillStyle = '#000';
      ctx.textAlign = 'center';
      
      // Calculate dynamic font sizes based on table dimensions
      const textSizeMultiplier = table.textSize || 1.0; // Default to 1.0 if not set
      const baseFontSize = Math.min(table.width, table.height) / 5;
      const nameFontSize = Math.max(10, Math.min(18, baseFontSize * textSizeMultiplier));
      const seatsFontSize = Math.max(8, Math.min(14, baseFontSize * 0.8 * textSizeMultiplier));
      const customerFontSize = Math.max(7, Math.min(12, baseFontSize * 0.7 * textSizeMultiplier));
      
      // Line 1: Table name (bold, larger)
      ctx.font = `bold ${nameFontSize}px Inter, sans-serif`;
      ctx.fillText(table.name, 0, -baseFontSize * 0.7);
      
      // Line 2: Seats count
      ctx.font = `${seatsFontSize}px Inter, sans-serif`;
      ctx.fillText(`${table.seats} seats`, 0, baseFontSize * 0.3);
      
      // Line 3: Customer name if reserved/occupied
      if ((table.status === 'reserved' || table.status === 'occupied') && table.customer) {
        ctx.font = `bold ${customerFontSize}px Inter, sans-serif`;
        const customerName = table.customer.name.length > 15 
          ? table.customer.name.substring(0, 15) + '...' 
          : table.customer.name;
        ctx.fillText(customerName, 0, baseFontSize * 1.2);
        
        // Line 4: Guest Count if customer exists
        if (table.customer.guestNo) {
          ctx.font = '9px Inter, sans-serif';
          ctx.fillText(`Guests: ${table.customer.guestNo}`, 0, 28);
        }
      }
      
      ctx.restore();
    });

    // Draw merge group connections (visual indicators)
    mergeGroups.forEach(group => {
      if (group.tableIds.length < 2) return;
      
      const groupTables = currentFloorPlan.tables.filter(t => group.tableIds.includes(t.id));
      if (groupTables.length < 2) return;
      
      // Draw connections between merged tables
      ctx.strokeStyle = group.color || '#10B981';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]); // Dashed line
      ctx.globalAlpha = 0.6;
      
      for (let i = 0; i < groupTables.length - 1; i++) {
        const table1 = groupTables[i];
        const table2 = groupTables[i + 1];
        
        ctx.beginPath();
        ctx.moveTo(table1.xposition + table1.width / 2, table1.yposition + table1.height / 2);
        ctx.lineTo(table2.xposition + table2.width / 2, table2.yposition + table2.height / 2);
        ctx.stroke();
      }
      
      ctx.setLineDash([]); // Reset to solid line
      ctx.globalAlpha = 1.0;
    });

    // Draw resize handles and rotation handles in edit mode
    if (viewMode === 'edit' && selectedElement && selectedElementType) {
      if (selectedElementType === 'table') {
        const table = currentFloorPlan.tables.find(t => t.id === selectedElement);
        if (table) {
          // Draw 8 resize handles
          const handleSize = 8;
          ctx.fillStyle = '#8b5cf6';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          
          const handles = [
            { xposition: table.xposition, yposition: table.yposition }, // nw
            { xposition: table.xposition + table.width / 2, yposition: table.yposition }, // n
            { xposition: table.xposition + table.width, yposition: table.yposition }, // ne
            { xposition: table.xposition + table.width, yposition: table.yposition + table.height / 2 }, // e
            { xposition: table.xposition + table.width, yposition: table.yposition + table.height }, // se
            { xposition: table.xposition + table.width / 2, yposition: table.yposition + table.height }, // s
            { xposition: table.xposition, yposition: table.yposition + table.height }, // sw
            { xposition: table.xposition, yposition: table.yposition + table.height / 2 }, // w
          ];
          
          handles.forEach(handle => {
            ctx.fillRect(handle.xposition - handleSize / 2, handle.yposition - handleSize / 2, handleSize, handleSize);
            ctx.strokeRect(handle.xposition - handleSize / 2, handle.yposition - handleSize / 2, handleSize, handleSize);
          });
          
          // Draw rotation handle (circular, top-right corner)
          const rotateX = table.xposition + table.width + 10;
          const rotateY = table.yposition - 10;
          ctx.beginPath();
          ctx.arc(rotateX, rotateY, 8, 0, 2 * Math.PI);
          ctx.fillStyle = '#10b981';
          ctx.fill();
          ctx.stroke();
          
          // Draw rotation icon (↻)
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 10px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('↻', rotateX, rotateY);
        }
      } else if (selectedElementType === 'area') {
        const area = currentFloorPlan.areas.find(a => a.id === selectedElement);
        if (area) {
          // Draw 8 resize handles
          const handleSize = 8;
          ctx.fillStyle = '#8b5cf6';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          
          const handles = [
            { xposition: area.xposition, yposition: area.yposition }, // nw
            { xposition: area.xposition + area.width / 2, yposition: area.yposition }, // n
            { xposition: area.xposition + area.width, yposition: area.yposition }, // ne
            { xposition: area.xposition + area.width, yposition: area.yposition + area.height / 2 }, // e
            { xposition: area.xposition + area.width, yposition: area.yposition + area.height }, // se
            { xposition: area.xposition + area.width / 2, yposition: area.yposition + area.height }, // s
            { xposition: area.xposition, yposition: area.yposition + area.height }, // sw
            { xposition: area.xposition, yposition: area.yposition + area.height / 2 }, // w
          ];
          
          handles.forEach(handle => {
            ctx.fillRect(handle.xposition - handleSize / 2, handle.yposition - handleSize / 2, handleSize, handleSize);
            ctx.strokeRect(handle.xposition - handleSize / 2, handle.yposition - handleSize / 2, handleSize, handleSize);
          });
          
          // Draw rotation handle (circular, top-right corner)
          const rotateX = area.xposition + area.width + 10;
          const rotateY = area.yposition - 10;
          ctx.beginPath();
          ctx.arc(rotateX, rotateY, 8, 0, 2 * Math.PI);
          ctx.fillStyle = '#10b981';
          ctx.fill();
          ctx.stroke();
          
          // Draw rotation icon (↻)
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 10px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('↻', rotateX, rotateY);
        }
      } else if (selectedElementType === 'divider') {
        const divider = currentFloorPlan.dividers.find(d => d.id === selectedElement);
        if (divider) {
          // Draw endpoint handles
          const handleSize = 10;
          ctx.fillStyle = '#8b5cf6';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          
          // Start point handle
          ctx.beginPath();
          ctx.arc(divider.x1position, divider.y1position, handleSize / 2, 0, 2 * Math.PI);
          ctx.fill();
          ctx.stroke();
          
          // End point handle
          ctx.beginPath();
          ctx.arc(divider.x2position, divider.y2position, handleSize / 2, 0, 2 * Math.PI);
          ctx.fill();
          ctx.stroke();
          
          // Draw rotation handle at center
          const centerX = (divider.x1position + divider.x2position) / 2;
          const centerY = (divider.y1position + divider.y2position) / 2;
          const rotateX = centerX + 15;
          const rotateY = centerY - 15;
          
          ctx.beginPath();
          ctx.arc(rotateX, rotateY, 8, 0, 2 * Math.PI);
          ctx.fillStyle = '#10b981';
          ctx.fill();
          ctx.stroke();
          
          // Draw rotation icon (↻)
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 10px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('↻', rotateX, rotateY);
        }
      }
    }

    // Restore context after drawing
    ctx.restore();
  }, [currentFloorPlan, selectedElement, selectedElementType, viewMode, selectedTablesForMerge, mergeGroups, zoomLevel, canvasPan]);

  // CRUD Functions
  const addNewTable = () => {
    const newTable: Table = {
      id: `t${Date.now()}`,
      name: `T${currentFloorPlan.tables.length + 1}`,
      xposition: 50,
      yposition: 50,
      width: 150,
      height: 150,
      seats: 4,
      status: 'available',
      shape: 'rectangle',
      description: 'rectangle table', // Auto-generated description
      rotation: 0,
      lastUpdated: new Date()
    };

    setFloorPlans(prev => ({
      ...prev,
      [currentFloor]: {
        ...prev[currentFloor],
        tables: [...prev[currentFloor].tables, newTable]
      }
    }));

    // Auto-select the new table for immediate editing
    setSelectedElement(newTable.id);
    setSelectedElementType('table');

    toast.success('Table added! Edit properties in the right panel');
  };

  const addNewArea = () => {
    // Generate unique name with floor prefix and timestamp to avoid backend conflicts
    const floorPrefix = currentFloor === 'floor1' ? 'F1' : currentFloor === 'floor2' ? 'F2' : 'RT';
    const uniqueSuffix = Date.now().toString().slice(-6); // Last 6 digits of timestamp
    const newArea: FloorArea = {
      id: `area-${Date.now()}`,
      name: `${floorPrefix}-Area-${uniqueSuffix}`,
      xposition: 200,
      yposition: 200,
      width: 320,
      height: 280,
      type: 'dining',
      color: '#e3f2fd'
    };

    setFloorPlans(prev => ({
      ...prev,
      [currentFloor]: {
        ...prev[currentFloor],
        areas: [...prev[currentFloor].areas, newArea]
      }
    }));

    // Mark as unsaved changes
    setUnsavedChanges(true);

    // Auto-select the new area for immediate editing
    setSelectedElement(newArea.id);
    setSelectedElementType('area');

    toast.success('Area added! Click Save to persist changes.');
  };

  const addNewDivider = () => {
    const newDivider: Divider = {
      id: `divider-${Date.now()}`,
      x1position: 300,
      y1position: 100,
      x2position: 400,
      y2position: 100,
      thickness: 10,
      color: '#8b5cf6',
      type: 'wall',
      rotation: 0
    };

    setFloorPlans(prev => ({
      ...prev,
      [currentFloor]: {
        ...prev[currentFloor],
        dividers: [...prev[currentFloor].dividers, newDivider]
      }
    }));

    // Mark as unsaved changes
    setUnsavedChanges(true);

    toast.success('Divider added! Click Save to persist changes.');
  };

  const deleteSelectedElement = async () => {
    if (!selectedElement || !selectedElementType) return;

    // For tables, show confirmation dialog with reservation check
    if (selectedElementType === 'table') {
      const table = currentFloorPlan.tables.find(t => t.id === selectedElement);
      if (!table) return;

      const numericId = parseInt(selectedElement.toString());
      if (!isNaN(numericId) && numericId > 0) {
        try {
          // Check for reservations first
          const reservationCheck = await tableApi.checkTableReservations(numericId);
          
          setDeleteTableInfo({
            id: selectedElement,
            name: table.name,
            hasReservations: reservationCheck.hasReservations,
            reservationCount: reservationCheck.count,
            reservationDetails: reservationCheck.details.map(detail => {
              // Parse the detail string to extract components
              const parts = detail.split(' ');
              return {
                status: parts[0],
                guestNo: parseInt(parts[3]) || 0,
                date: parts[7] || '',
                time: parts[9] || '',
                username: parts.slice(10).join(' ').replace(/[()]/g, '') || 'Unknown'
              };
            })
          });
          setShowDeleteConfirmation(true);
        } catch (error) {
          //console.error('Failed to check table reservations:', error);
          toast.error('Failed to check table reservations. Please try again.');
        }
      }
      return;
    }

    // For areas and dividers, delete directly (no reservation conflicts)
    await performElementDeletion();
  };
  const performElementDeletion = async () => {
    if (!selectedElement || !selectedElementType) return;

    try {
      // Delete from backend first
      if (selectedElementType === 'table') {
        const numericId = parseInt(selectedElement.toString());
        if (!isNaN(numericId) && numericId > 0) {
          await tableApi.deleteTable(numericId);
          //console.log(`✅ Table ${numericId} deleted from backend`);
        }
      } else if (selectedElementType === 'area') {
        const numericId = parseInt(selectedElement.toString());
        if (!isNaN(numericId) && numericId > 0) {
          await areaApi.deleteArea(numericId);
          //console.log(`✅ Area ${numericId} deleted from backend`);
        }
      } else if (selectedElementType === 'divider') {
        const numericId = parseInt(selectedElement.toString());
        if (!isNaN(numericId) && numericId > 0) {
          await dividerApi.deleteDivider(numericId);
          //console.log(`✅ Divider ${numericId} deleted from backend`);
        }
      }

      // Update local state
      setFloorPlans(prev => {
        const currentPlan = prev[currentFloor];
        const updatedPlan = { ...currentPlan };

        switch (selectedElementType) {
          case 'table':
            updatedPlan.tables = currentPlan.tables.filter(t => t.id !== selectedElement);
            break;
          case 'area':
            updatedPlan.areas = currentPlan.areas.filter(a => a.id !== selectedElement);
            break;
          case 'divider':
            updatedPlan.dividers = currentPlan.dividers.filter(d => d.id !== selectedElement);
            break;
        }

        return {
          ...prev,
          [currentFloor]: updatedPlan
        };
      });

      setSelectedElement(null);
      setSelectedElementType(null);
      setUnsavedChanges(true); // Mark for areas/dividers save
      toast.success(`${selectedElementType === 'table' ? 'Table' : selectedElementType === 'area' ? 'Area' : 'Divider'} deleted successfully`);
    } catch (error) {
      //console.error('Failed to delete element:', error);
      toast.error(`Failed to delete ${selectedElementType}`);
    }
  };

  // Handle merge group creation from preview dialog
  const handleMergeConfirm = (groupName: string, color?: string) => {
    const selectedTables = currentFloorPlan.tables.filter(t => 
      selectedTablesForMerge.includes(t.id)
    );
    const totalCapacity = selectedTables.reduce((sum, t) => sum + t.seats, 0);
    
    // Format tables for backend API - restaurantTableId is now an array of objects
    const restaurantTableId = selectedTables.map(table => ({
      id: parseInt(table.id.replace(/\D/g, '')) || 0,
      name: table.name
    })).filter(t => t.id > 0); // Only include tables with valid numeric IDs
    
    const newGroup: MergeGroup = {
      id: `merge_${Date.now()}`,
      name: groupName,
      tableIds: selectedTablesForMerge,
      totalCapacity,
      restaurantTableId, // Backend expects array: [{id: number, name: string}]
      color
    };
    
    setMergeGroups([...mergeGroups, newGroup]);
    setSelectedTablesForMerge([]);
    setIsSelectionMode(false);
    
    // Note: Merge groups are created via booking flow in AddReservationDialog
    // with mergeGroupName + mergeGroupColor fields, not through direct API
    // console.log('Merge group created locally. Use booking flow for backend persistence:', {
    //   name: newGroup.name,
    //   seats: totalCapacity,
    //   restaurantTableId, // Array format: [{id: 2, name: "T2"}, {id: 3, name: "T3"}]
    //   color
    // });
  };

  // Handle merge preview cancel
  const handleMergeCancel = () => {
    // Keep selection for user to modify if needed
    setShowMergePreview(false);
  };

  // Event handlers
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    // Scale coordinates from display size to canvas size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const screenX = (e.clientX - rect.left) * scaleX;
    const screenY = (e.clientY - rect.top) * scaleY;

    // Convert screen coordinates to canvas coordinates accounting for zoom and pan
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const xposition = (screenX - centerX) / zoomLevel + centerX - canvasPan.x / zoomLevel;
    const yposition = (screenY - centerY) / zoomLevel + centerY - canvasPan.y / zoomLevel;

    // In static mode, handle table selection for merging
    if (viewMode === 'static') {
      const clickedTable = currentFloorPlan.tables.find(table =>
        xposition >= table.xposition && xposition <= table.xposition + table.width &&
        yposition >= table.yposition && yposition <= table.yposition + table.height
      );
      
      if (clickedTable) {
        // If Ctrl/Cmd is pressed or selection mode is active, toggle table selection
        if (e.ctrlKey || e.metaKey || isSelectionMode) {
          setSelectedTablesForMerge(prev => {
            const isAlreadySelected = prev.includes(clickedTable.id);
            const newSelection = isAlreadySelected
              ? prev.filter(id => id !== clickedTable.id)
              : [...prev, clickedTable.id];
            toast.success(
              isAlreadySelected
                ? `Deselected ${clickedTable.name}`
                : `Selected ${clickedTable.name}`,
              { duration: 1500 }
            );
            return newSelection;
          });
        } else {
          // Otherwise, show table details and fetch booking info + all reservations
          setShowTableDetails(clickedTable.id);
          fetchTableBookingDetails(clickedTable.id);
          fetchTableReservations(clickedTable.id);
        }
        return;
      }
      setShowTableDetails(null);
      return;
    }

    // Edit mode: check for tables first
    const clickedTable = currentFloorPlan.tables.find(table => {
      return xposition >= table.xposition && xposition <= table.xposition + table.width &&
             yposition >= table.yposition && yposition <= table.yposition + table.height;
    });

    if (clickedTable) {
      setSelectedElement(clickedTable.id);
      setSelectedElementType('table');
      return;
    }

    // Check areas
    const clickedArea = currentFloorPlan.areas.find(area =>
      xposition >= area.xposition && xposition <= area.xposition + area.width &&
      yposition >= area.yposition && yposition <= area.yposition + area.height
    );

    if (clickedArea) {
      setSelectedElement(clickedArea.id);
      setSelectedElementType('area');
      return;
    }

    // Check dividers
    const clickedDivider = currentFloorPlan.dividers.find(divider => {
      const tolerance = Math.max(10, divider.thickness + 5);
      const dist = distanceToLine(xposition, yposition, divider.x1position, divider.y1position, divider.x2position, divider.y2position);
      return dist <= tolerance;
    });

    if (clickedDivider) {
      setSelectedElement(clickedDivider.id);
      setSelectedElementType('divider');
      return;
    }

    setSelectedElement(null);
    setSelectedElementType(null);
  };

  // Helper function to calculate distance from point to line
  const distanceToLine = (px: number, py: number, x1position: number, y1position: number, x2position: number, y2position: number) => {
    const A = px - x1position;
    const B = py - y1position;
    const C = x2position - x1position;
    const D = y2position - y1position;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    if (lenSq !== 0) {
      param = dot / lenSq;
    }

    let xx, yy;

    if (param < 0) {
      xx = x1position;
      yy = y1position;
    } else if (param > 1) {
      xx = x2position;
      yy = y2position;
    } else {
      xx = x1position + param * C;
      yy = y1position + param * D;
    }

    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
  };


  // Keep a baseline snapshot to send only changed entities and minimize API calls
  const baselineRef = useRef<Record<'floor1' | 'floor2' | 'rooftop', FloorPlan> | null>(null);

  // Auto-save disabled - only manual save allowed

  // Navigation warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (unsavedChanges) {
        const message = 'You have unsaved changes. Are you sure you want to leave?';
        e.returnValue = message;
        return message;
      }
    };

    const handlePopstate = () => {
      if (unsavedChanges) {
        const confirmLeave = globalThis.confirm('You have unsaved changes. Are you sure you want to leave?');
        if (!confirmLeave) {
          // Push the current state back to prevent navigation
          globalThis.history.pushState(null, '', globalThis.location.href);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopstate);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopstate);
    };
  }, [unsavedChanges]);

  // ========== DRAG & DROP & RESIZE & ROTATE HANDLERS ==========
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    // Scale coordinates from display size to canvas size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const screenX = (e.clientX - rect.left) * scaleX;
    const screenY = (e.clientY - rect.top) * scaleY;

    // Middle mouse button or Space key pressed - enable canvas panning
    if (e.button === 1 || e.shiftKey) {
      setIsPanning(true);
      setPanStart({ x: screenX - canvasPan.x, y: screenY - canvasPan.y });
      e.preventDefault();
      return;
    }

    if (viewMode !== 'edit' || !selectedElement || !selectedElementType) return;

    // Convert screen coordinates to canvas coordinates accounting for zoom and pan
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const mouseX = (screenX - centerX) / zoomLevel + centerX - canvasPan.x / zoomLevel;
    const mouseY = (screenY - centerY) / zoomLevel + centerY - canvasPan.y / zoomLevel;

    // Check for rotation handle first (top-right corner, circular handle)
    if (selectedElementType === 'table' || selectedElementType === 'area') {
      const element = selectedElementType === 'table'
        ? currentFloorPlan.tables.find(t => t.id === selectedElement)
        : currentFloorPlan.areas.find(a => a.id === selectedElement);
      
      if (element) {
        const rotateHandleX = element.xposition + element.width + 10;
        const rotateHandleY = element.yposition - 10;
        const distToRotateHandle = Math.sqrt(
          Math.pow(mouseX - rotateHandleX, 2) + Math.pow(mouseY - rotateHandleY, 2)
        );
        
        if (distToRotateHandle <= 8) {
          setIsRotating(true);
          const centerX = element.xposition + element.width / 2;
          const centerY = element.yposition + element.height / 2;
          setRotationStart({ angle: element.rotation || 0, centerX, centerY });
          return;
        }
      }
    } else if (selectedElementType === 'divider') {
      const divider = currentFloorPlan.dividers.find(d => d.id === selectedElement);
      if (divider) {
        const centerX = (divider.x1position + divider.x2position) / 2;
        const centerY = (divider.y1position + divider.y2position) / 2;
        const rotateHandleX = centerX + 15;
        const rotateHandleY = centerY - 15;
        const distToRotateHandle = Math.sqrt(
          Math.pow(mouseX - rotateHandleX, 2) + Math.pow(mouseY - rotateHandleY, 2)
        );
        
        if (distToRotateHandle <= 8) {
          setIsRotating(true);
          setRotationStart({ angle: divider.rotation || 0, centerX, centerY });
          return;
        }
      }
    }

    // Check for resize handles (8-point handles for tables and areas)
    if (selectedElementType === 'table') {
      const table = currentFloorPlan.tables.find(t => t.id === selectedElement);
      if (table) {
        const handle = getResizeHandle(
          { xposition: mouseX, yposition: mouseY },
          { xposition: table.xposition, yposition: table.yposition, width: table.width, height: table.height }
        );
        if (handle) {
          setIsResizing(true);
          setResizeHandle(handle);
          setResizeStart({ xposition: mouseX, yposition: mouseY, width: table.width, height: table.height });
          return;
        }
        
        // Start dragging if clicked inside table
        if (mouseX >= table.xposition && mouseX <= table.xposition + table.width &&
            mouseY >= table.yposition && mouseY <= table.yposition + table.height) {
          setIsDragging(true);
          setDragOffset({ xposition: mouseX - table.xposition, yposition: mouseY - table.yposition });
        }
      }
    } else if (selectedElementType === 'area') {
      const area = currentFloorPlan.areas.find(a => a.id === selectedElement);
      if (area) {
        const handle = getResizeHandle(
          { xposition: mouseX, yposition: mouseY },
          { xposition: area.xposition, yposition: area.yposition, width: area.width, height: area.height }
        );
        if (handle) {
          setIsResizing(true);
          setResizeHandle(handle);
          setResizeStart({ xposition: mouseX, yposition: mouseY, width: area.width, height: area.height });
          return;
        }

        if (mouseX >= area.xposition && mouseX <= area.xposition + area.width &&
            mouseY >= area.yposition && mouseY <= area.yposition + area.height) {
          setIsDragging(true);
          setDragOffset({ xposition: mouseX - area.xposition, yposition: mouseY - area.yposition });
        }
      }
    } else if (selectedElementType === 'divider') {
      // Dividers: resize by dragging endpoints
      const divider = currentFloorPlan.dividers.find(d => d.id === selectedElement);
      if (divider) {
        const distToStart = Math.sqrt(Math.pow(mouseX - divider.x1position, 2) + Math.pow(mouseY - divider.y1position, 2));
        const distToEnd = Math.sqrt(Math.pow(mouseX - divider.x2position, 2) + Math.pow(mouseY - divider.y2position, 2));
        
        if (distToStart <= 10) {
          setIsResizing(true);
          setResizeHandle('start');
          return;
        } else if (distToEnd <= 10) {
          setIsResizing(true);
          setResizeHandle('end');
          return;
        }
        
        // Drag entire divider
        setIsDragging(true);
        setDragOffset({ xposition: mouseX - divider.x1position, yposition: mouseY - divider.y1position });
      }
    }
  }, [viewMode, selectedElement, selectedElementType, currentFloorPlan, zoomLevel, canvasPan]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    // Scale coordinates from display size to canvas size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const screenX = (e.clientX - rect.left) * scaleX;
    const screenY = (e.clientY - rect.top) * scaleY;

    // Handle canvas panning
    if (isPanning) {
      setCanvasPan({
        x: screenX - panStart.x,
        y: screenY - panStart.y
      });
      canvas.style.cursor = 'grabbing';
      return;
    }

    // Convert screen coordinates to canvas coordinates accounting for zoom and pan
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const mouseX = (screenX - centerX) / zoomLevel + centerX - canvasPan.x / zoomLevel;
    const mouseY = (screenY - centerY) / zoomLevel + centerY - canvasPan.y / zoomLevel;

    // Update cursor based on hover state - resize handles, rotation handles, or move
    if (viewMode === 'edit' && selectedElement && selectedElementType) {
      // Check for rotation handle hover
      if (selectedElementType === 'table' || selectedElementType === 'area') {
        const element = selectedElementType === 'table'
          ? currentFloorPlan.tables.find(t => t.id === selectedElement)
          : currentFloorPlan.areas.find(a => a.id === selectedElement);
        
        if (element) {
          const rotateHandleX = element.xposition + element.width + 10;
          const rotateHandleY = element.yposition - 10;
          const distToRotateHandle = Math.sqrt(
            Math.pow(mouseX - rotateHandleX, 2) + Math.pow(mouseY - rotateHandleY, 2)
          );
          
          if (distToRotateHandle <= 8) {
            canvas.style.cursor = 'grab';
          } else {
            // Check for resize handles
            const handle = getResizeHandle(
              { xposition: mouseX, yposition: mouseY },
              { xposition: element.xposition, yposition: element.yposition, width: element.width, height: element.height }
            );
            canvas.style.cursor = handle ? getResizeCursor(handle) : 'move';
          }
        }
      } else if (selectedElementType === 'divider') {
        const divider = currentFloorPlan.dividers.find(d => d.id === selectedElement);
        if (divider) {
          const centerX = (divider.x1position + divider.x2position) / 2;
          const centerY = (divider.y1position + divider.y2position) / 2;
          const rotateHandleX = centerX + 15;
          const rotateHandleY = centerY - 15;
          const distToRotateHandle = Math.sqrt(
            Math.pow(mouseX - rotateHandleX, 2) + Math.pow(mouseY - rotateHandleY, 2)
          );
          
          if (distToRotateHandle <= 8) {
            canvas.style.cursor = 'grab';
          } else {
            const distToStart = Math.sqrt(Math.pow(mouseX - divider.x1position, 2) + Math.pow(mouseY - divider.y1position, 2));
            const distToEnd = Math.sqrt(Math.pow(mouseX - divider.x2position, 2) + Math.pow(mouseY - divider.y2position, 2));
            canvas.style.cursor = (distToStart <= 10 || distToEnd <= 10) ? 'pointer' : 'move';
          }
        }
      }
    } else {
      canvas.style.cursor = 'default';
    }

    // Handle rotation
    if (isRotating && selectedElement && selectedElementType) {
      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Schedule update on next animation frame for smooth rendering
      animationFrameRef.current = requestAnimationFrame(() => {
        const dx = mouseX - rotationStart.centerX;
        const dy = mouseY - rotationStart.centerY;
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        const newRotation = angle;

        setFloorPlans(prev => {
          const currentPlan = prev[currentFloor];
          const updatedPlan = { ...currentPlan };

          if (selectedElementType === 'table') {
            updatedPlan.tables = currentPlan.tables.map(table =>
              table.id === selectedElement
                ? { ...table, rotation: newRotation, lastUpdated: new Date() }
                : table
            );
          } else if (selectedElementType === 'area') {
            updatedPlan.areas = currentPlan.areas.map(area =>
              area.id === selectedElement
                ? { ...area, rotation: newRotation }
                : area
            );
          } else if (selectedElementType === 'divider') {
            updatedPlan.dividers = currentPlan.dividers.map(divider =>
              divider.id === selectedElement
                ? { ...divider, rotation: newRotation }
                : divider
            );
          }

          return { ...prev, [currentFloor]: updatedPlan };
        });

        setUnsavedChanges(true);
        // Auto-save disabled - manual save required
      });
    }

    // Handle dragging
    if (isDragging && selectedElement && selectedElementType) {
      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Schedule update on next animation frame for smooth rendering
      animationFrameRef.current = requestAnimationFrame(() => {
        const newX = Math.max(0, Math.min(canvas.width - 80, mouseX - dragOffset.xposition));
        const newY = Math.max(0, Math.min(canvas.height - 80, mouseY - dragOffset.yposition));

        setFloorPlans(prev => {
          const currentPlan = prev[currentFloor];
          const updatedPlan = { ...currentPlan };

          if (selectedElementType === 'table') {
            updatedPlan.tables = currentPlan.tables.map(table =>
              table.id === selectedElement
                ? { ...table, xposition: newX, yposition: newY, lastUpdated: new Date() }
                : table
            );
          } else if (selectedElementType === 'area') {
            updatedPlan.areas = currentPlan.areas.map(area =>
              area.id === selectedElement
                ? { ...area, xposition: newX, yposition: newY }
                : area
            );
          } else if (selectedElementType === 'divider') {
            updatedPlan.dividers = currentPlan.dividers.map(divider => {
              if (divider.id === selectedElement) {
                const dx = newX - divider.x1position;
                const dy = newY - divider.y1position;
                return {
                  ...divider,
                  x1position: newX,
                  y1position: newY,
                  x2position: divider.x2position + dx,
                  y2position: divider.y2position + dy
                };
              }
              return divider;
            });
          }

          return { ...prev, [currentFloor]: updatedPlan };
        });

        setUnsavedChanges(true);
        // Auto-save disabled - manual save required
      });
    }

    // Handle resizing
    if (isResizing && selectedElement && selectedElementType && resizeHandle) {
      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Schedule update on next animation frame for smooth rendering
      animationFrameRef.current = requestAnimationFrame(() => {
        setFloorPlans(prev => {
          const currentPlan = prev[currentFloor];
          const updatedPlan = { ...currentPlan };

          if (selectedElementType === 'table') {
            updatedPlan.tables = currentPlan.tables.map(table => {
              if (table.id === selectedElement) {
                const result = calculateResize(
                  { xposition: table.xposition, yposition: table.yposition, width: table.width, height: table.height },
                  { xposition: mouseX, yposition: mouseY },
                  resizeStart,
                  resizeHandle
                );
                return { ...table, ...result, lastUpdated: new Date() };
              }
              return table;
            });
          } else if (selectedElementType === 'area') {
            updatedPlan.areas = currentPlan.areas.map(area => {
              if (area.id === selectedElement) {
                const result = calculateResize(
                  { xposition: area.xposition, yposition: area.yposition, width: area.width, height: area.height },
                  { xposition: mouseX, yposition: mouseY },
                  resizeStart,
                  resizeHandle
                );
                return { ...area, ...result };
              }
              return area;
            });
          } else if (selectedElementType === 'divider') {
            updatedPlan.dividers = currentPlan.dividers.map(divider => {
              if (divider.id === selectedElement) {
                if (resizeHandle === 'start') {
                  return { ...divider, x1position: mouseX, y1position: mouseY };
                } else if (resizeHandle === 'end') {
                  return { ...divider, x2position: mouseX, y2position: mouseY };
                }
              }
              return divider;
            });
          }

          return { ...prev, [currentFloor]: updatedPlan };
        });

        setUnsavedChanges(true);
        // Auto-save disabled - manual save required
      });
    }
  }, [viewMode, selectedElement, selectedElementType, isDragging, isResizing, isRotating, dragOffset, resizeHandle, resizeStart, rotationStart, currentFloor, currentFloorPlan, zoomLevel, isPanning, canvasPan, panStart]);

  const handleMouseUp = useCallback(() => {
    // Stop canvas panning
    if (isPanning) {
      setIsPanning(false);
      return;
    }

    if (isDragging || isResizing || isRotating) {
      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      setIsDragging(false);
      setIsResizing(false);
      setIsRotating(false);
      setResizeHandle(null);
      
      // Auto-save disabled - manual save required
      // User must click Save button to persist changes
    }
  }, [isDragging, isResizing, isRotating, isPanning]);

  // ========== RESIZE & ROTATION HELPER FUNCTIONS ==========
  const getResizeHandle = (
    mouse: { xposition: number; yposition: number },
    element: { xposition: number; yposition: number; width: number; height: number }
  ) => {
    const handleSize = 10;
    const tolerance = handleSize / 2;

    // Corner handles (priority)
    if (Math.abs(mouse.xposition - element.xposition) <= tolerance && Math.abs(mouse.yposition - element.yposition) <= tolerance) return 'nw';
    if (Math.abs(mouse.xposition - (element.xposition + element.width)) <= tolerance && Math.abs(mouse.yposition - element.yposition) <= tolerance) return 'ne';
    if (Math.abs(mouse.xposition - element.xposition) <= tolerance && Math.abs(mouse.yposition - (element.yposition + element.height)) <= tolerance) return 'sw';
    if (Math.abs(mouse.xposition - (element.xposition + element.width)) <= tolerance && Math.abs(mouse.yposition - (element.yposition + element.height)) <= tolerance) return 'se';

    // Edge handles
    if (Math.abs(mouse.yposition - element.yposition) <= tolerance && mouse.xposition >= element.xposition && mouse.xposition <= element.xposition + element.width) return 'n';
    if (Math.abs(mouse.yposition - (element.yposition + element.height)) <= tolerance && mouse.xposition >= element.xposition && mouse.xposition <= element.xposition + element.width) return 's';
    if (Math.abs(mouse.xposition - element.xposition) <= tolerance && mouse.yposition >= element.yposition && mouse.yposition <= element.yposition + element.height) return 'w';
    if (Math.abs(mouse.xposition - (element.xposition + element.width)) <= tolerance && mouse.yposition >= element.yposition && mouse.yposition <= element.yposition + element.height) return 'e';

    return null;
  };

  const getResizeCursor = (handle: string) => {
    const cursors: Record<string, string> = {
      n: 'n-resize',
      s: 's-resize',
      e: 'e-resize',
      w: 'w-resize',
      ne: 'ne-resize',
      nw: 'nw-resize',
      se: 'se-resize',
      sw: 'sw-resize'
    };
    return cursors[handle] || 'default';
  };

  const calculateResize = (
    element: { xposition: number; yposition: number; width: number; height: number },
    mouse: { xposition: number; yposition: number },
    startState: { xposition: number; yposition: number; width: number; height: number },
    handle: string
  ) => {
    const minSize = 40;
    const dx = mouse.xposition - startState.xposition;
    const dy = mouse.yposition - startState.yposition;

    let newX = element.xposition;
    let newY = element.yposition;
    let newWidth = element.width;
    let newHeight = element.height;

    switch (handle) {
      case 'se':
        newWidth = Math.max(minSize, startState.width + dx);
        newHeight = Math.max(minSize, startState.height + dy);
        break;
      case 'sw':
        newWidth = Math.max(minSize, startState.width - dx);
        newHeight = Math.max(minSize, startState.height + dy);
        newX = element.xposition + (element.width - newWidth);
        break;
      case 'ne':
        newWidth = Math.max(minSize, startState.width + dx);
        newHeight = Math.max(minSize, startState.height - dy);
        newY = element.yposition + (element.height - newHeight);
        break;
      case 'nw':
        newWidth = Math.max(minSize, startState.width - dx);
        newHeight = Math.max(minSize, startState.height - dy);
        newX = element.xposition + (element.width - newWidth);
        newY = element.yposition + (element.height - newHeight);
        break;
      case 'e':
        newWidth = Math.max(minSize, startState.width + dx);
        break;
      case 'w':
        newWidth = Math.max(minSize, startState.width - dx);
        newX = element.xposition + (element.width - newWidth);
        break;
      case 'n':
        newHeight = Math.max(minSize, startState.height - dy);
        newY = element.yposition + (element.height - newHeight);
        break;
      case 's':
        newHeight = Math.max(minSize, startState.height + dy);
        break;
    }

    return { xposition: newX, yposition: newY, width: newWidth, height: newHeight };
  };

  // Clean up timeout on unmount and disable auto-save
  useEffect(() => {
    const timeoutRef = saveTimeoutRef;
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // UI control functions
  const toggleViewMode = () => {
    if (viewMode === 'static') {
      setViewMode('edit');
      toast.success('Edit mode enabled');
    } else {
      setViewMode('static');
      setSelectedElement(null);
      toast.success('Static view enabled');
    }
  };

  // Helper functions for table details
  const getSelectedTable = () => {
    return showTableDetails ? currentFloorPlan.tables.find(t => t.id === showTableDetails) : null;
  };
  
  // Fetch booking details for a table
  const fetchTableBookingDetails = useCallback(async (tableId: string) => {
    try {
      const tableIdNum = parseInt(tableId, 10);
      if (isNaN(tableIdNum) || tableIdNum <= 0) {
        //console.log('Skipping booking fetch for non-numeric/invalid table ID:', tableId);
        return;
      }
      
      const booking = await reservationApi.getReservationByTableAndDate(tableIdNum);
      setTableBookingDetails(prev => ({
        ...prev,
        [tableId]: booking
      }));
      
     // console.log(`📋 Booking details for table ${tableId}:`, booking);
    } catch (error) {
      //console.error('Failed to fetch booking details:', error);
      setTableBookingDetails(prev => ({
        ...prev,
        [tableId]: null
      }));
    }
  }, []);

  // Fetch all reservations for a specific table
  const fetchTableReservations = useCallback(async (tableId: string) => {
    try {
      const tableIdNum = parseInt(tableId, 10);
      if (isNaN(tableIdNum) || tableIdNum <= 0) {
        //console.log('Skipping reservations fetch for non-numeric/invalid table ID:', tableId);
        return;
      }
      
      // Get today's date
      const today = new Date().toISOString().split('T')[0];
      
      // Get all reservations for today
      const todayReservations = await reservationApi.searchReservationsByPeriodWithParams(today, 'today');
      
      // Filter reservations for this specific table
      const tableSpecificReservations = todayReservations.filter(reservation =>
        reservation.restaurantTableId?.some(table => table.id === tableIdNum)
      );
      
      setTableReservations(prev => ({
        ...prev,
        [tableId]: tableSpecificReservations
      }));
      
      //console.log(`📋 All reservations for table ${tableId}:`, tableSpecificReservations);
    } catch (error) {
      //console.error('Failed to fetch table reservations:', error);
      setTableReservations(prev => ({
        ...prev,
        [tableId]: []
      }));
    }
  }, []);

  const changeTableStatus = async (tableId: string, newStatus: Table['status']) => {
    // Find the table to get its current data
    const table = floorPlans[currentFloor].tables.find(t => t.id === tableId);
    if (!table) {
      toast.error(`Table ${tableId} not found`);
      return;
    }

    // Don't save new/temp tables to backend
    if (tableId.startsWith('new-') || tableId.startsWith('temp-')) {
      // Just update local state for new tables
      setFloorPlans(prev => ({
        ...prev,
        [currentFloor]: {
          ...prev[currentFloor],
          tables: prev[currentFloor].tables.map(t =>
            t.id === tableId
              ? { 
                  ...t, 
                  status: newStatus, 
                  lastUpdated: new Date(),
                  customer: undefined
                }
              : t
          )
        }
      }));
      toast.success(`Table ${tableId} status changed to ${newStatus}`);
      return;
    }

    // Update backend for existing tables
    try {
      const numericId = parseInt(tableId);
      if (isNaN(numericId) || numericId <= 0) {
        toast.error(`Invalid table ID: ${tableId}`);
        return;
      }

      // Map local Table type to backend RestaurantTable type
      const backendStatus = (newStatus.charAt(0).toUpperCase() + newStatus.slice(1)) as RestaurantTable['status'];
      const floorNumber = currentFloor === 'floor1' ? 1 : currentFloor === 'floor2' ? 2 : 3;

      const updatedTable: RestaurantTable = {
        id: numericId,
        name: table.name,
        floor: currentFloor === 'floor1' ? '1' : currentFloor === 'floor2' ? '2' : '3',
        seats: table.seats,
        status: backendStatus,
        xposition: table.xposition,
        yposition: table.yposition,
        location: currentFloorPlan.name || 'Main Floor',
        description: `${table.shape.toLowerCase()} table`,
        shape: table.shape,
        color: getStatusColor(newStatus),
        floorId: floorNumber,
        width: table.width,
        height: table.height,
        rotation: table.rotation
      };

      await tableApi.updateTable(updatedTable);

      // Update local state after successful backend update
      setFloorPlans(prev => ({
        ...prev,
        [currentFloor]: {
          ...prev[currentFloor],
          tables: prev[currentFloor].tables.map(t =>
            t.id === tableId
              ? { 
                  ...t, 
                  status: newStatus, 
                  lastUpdated: new Date(),
                  customer: undefined
                }
              : t
          )
        }
      }));
      
      toast.success(`Table ${tableId} status changed to ${newStatus}`);
    } catch (error) {
      //console.error('Failed to update table status:', error);
      toast.error(`Failed to update table status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Minimal Status Legend at Top */}
      {viewMode === 'static' && (
        <div className="bg-white border-b border-gray-200 px-6 py-2">
          <div className="flex items-center justify-center gap-6 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-green-500"></div>
              <span>Available</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-red-500"></div>
              <span>Occupied</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-yellow-500"></div>
              <span>Reserved</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-gray-500"></div>
              <span>Maintenance</span>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Changes Warning Banner */}
      {unsavedChanges && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
          <div className="flex items-center justify-between">
            <div className="flex">
              <Clock className="w-5 h-5 text-amber-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  You have unsaved changes
                </p>
                <p className="text-xs text-amber-600">
                  Auto-save is disabled. Click Save to persist your changes.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUnsavedChanges(false)}
                className="text-amber-800 border-amber-300 hover:bg-amber-100"
              >
                Discard
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={saveFloorPlan}
                disabled={isSaving}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {isSaving ? 'Saving...' : 'Save Now'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="flex items-center gap-4">
          <Building2 className="w-6 h-6 text-purple-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Floor View</h1>
          </div>
        </div>

        {/* Floor Selection */}
        <div className="flex items-center gap-3">
          <Button
            variant={currentFloor === 'floor1' ? 'blueBg' : 'outline'}
            onClick={() => switchToFloor('floor1')}
            size="sm"
            disabled={loadingFloors.floor1}
          >
            {loadingFloors.floor1 ? 'Loading...' : 'Floor 1'}
          </Button>
          <Button
            variant={currentFloor === 'floor2' ? 'blueBg' : 'outline'}
            onClick={() => switchToFloor('floor2')}
            size="sm"
            disabled={loadingFloors.floor2}
          >
            {loadingFloors.floor2 ? 'Loading...' : 'Floor 2'}
          </Button>
          <Button
            variant={currentFloor === 'rooftop' ? 'blueBg' : 'outline'}
            onClick={() => switchToFloor('rooftop')}
            size="sm"
            disabled={loadingFloors.rooftop}
          >
            {loadingFloors.rooftop ? 'Loading...' : 'Roof Top'}
          </Button>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          <Button
            variant={viewMode === 'edit' ? 'red' : 'outline'}
            onClick={toggleViewMode}
            size="sm"
          >
            {viewMode === 'edit' ? (
              <>
                <Eye className="w-4 h-4 mr-2" />
                View Mode
              </>
            ) : (
              <>
                <Edit3 className="w-4 h-4 mr-2" />
                Edit Mode
              </>
            )}
          </Button>

          {viewMode === 'edit' && (
            <>
              <Button variant={'outline'} onClick={saveFloorPlan} size="sm" disabled={isSaving}>
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              
              {unsavedChanges && !isSaving && (
                <Badge variant="secondary" className="ml-2">
                  Unsaved changes
                </Badge>
              )}
            </>
          )}
        </div>
      </div>

      {/* 3-Column Responsive Layout - Show in BOTH static and edit modes */}
      {/*
        Notes on layout fix:
        - Use horizontal scrolling when viewport becomes too narrow so right column isn't clipped
        - Allow right column to shrink when necessary by removing strict `flex-shrink-0` and adding `min-w-0`
      */}
      <div className="flex flex-col lg:flex-row flex-1 overflow-x-auto gap-0 min-h-0">
        {/* Column 1: Table Merge Section (Static) or Edit Tools (Edit) */}
        <div className="w-full lg:w-52 xl:w-56 bg-white dark:bg-gray-900 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 flex flex-col max-h-48 lg:max-h-none overflow-y-auto lg:overflow-y-auto flex-shrink-0">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {viewMode === 'static' ? 'Table Merge' : 'Edit Tools'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {viewMode === 'static' ? 'Merge tables for larger groups' : 'Add or modify elements'}
            </p>
          </div>
          <div className="flex-1 p-4 overflow-y-auto">
            {viewMode === 'static' ? (
              <div className="space-yposition-3">
                {/* Selection Mode Toggle */}
                <Button
                  variant={isSelectionMode ? 'red' : 'outline'}
                  className="w-full justify-start"
                  size="sm"
                  onClick={() => {
                    setIsSelectionMode(!isSelectionMode);
                    if (!isSelectionMode) {
                      toast.success('Selection mode enabled. Click tables to select them.', { duration: 2000 });
                    } else {
                      setSelectedTablesForMerge([]);
                      toast.success('Selection mode disabled', { duration: 1500 });
                    }
                  }}
                >
                  <PackagePlus className="w-4 h-4 mr-2 " />
                  {isSelectionMode ? 'Exit Selection Mode' : 'Select Tables'}
                </Button>

                {/* Show selected count */}
                {selectedTablesForMerge.length > 0 && (
                  <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm font-medium text-blue-900">
                      {selectedTablesForMerge.length} table{selectedTablesForMerge.length > 1 ? 's' : ''} selected
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-1 text-xs h-6"
                      onClick={() => {
                        setSelectedTablesForMerge([]);
                        toast.success('Selection cleared');
                      }}
                    >
                      Clear Selection
                    </Button>
                  </div>
                )}  
                
                {/* Merge Group Actions */}
                <div className="space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start mt-2" 
                    size="sm"
                    onClick={() => {
                      if (selectedTablesForMerge.length < 2) {
                        toast.error('Please select at least 2 tables to merge');
                        return;
                      }
                      // Open preview dialog instead of creating merge directly
                      setShowMergePreview(true);
                    }}
                    disabled={selectedTablesForMerge.length < 2}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Create Merge Group
                  </Button>
                  
                  {/* <Button 
                    variant="ghost" 
                    className="w-full justify-start text-purple-600 hover:text-purple-700 hover:bg-purple-50" 
                    size="sm"
                    onClick={() => setShowMergeHistory(true)}
                  >
                    <History className="w-4 h-4 mr-2" />
                    Merge History
                  </Button> */}
                </div>
                <div className="text-sm text-gray-500">
                  {mergeGroups.length === 0 
                    ? 'No active merge groups' 
                    : `${mergeGroups.length} active merge group${mergeGroups.length > 1 ? 's' : ''}`}
                </div>
                {mergeGroups.length > 0 && (
                  <div className="space-yposition-2 mt-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2">
                      Active Merge Groups
                    </div>
                    {mergeGroups.map(group => (
                      <div 
                        key={group.id} 
                        className="p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                        style={{
                          borderLeft: group.color ? `4px solid ${group.color}` : 'none'
                        }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-sm">{group.name}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={async () => {
                              try {
                                // Check if this is a backend merge group (has numeric ID) or local group (string ID)
                                const numericId = parseInt(group.id);
                                if (!isNaN(numericId) && numericId > 0) {
                                  // Backend merge group - call API to delete
                                  //console.log('🗑️ Deleting backend merge group:', numericId);
                                  // Use enum constant name 'CANCELED' instead of string value 'Canceled'
                                  await reservationApi.deleteMergeGroup(numericId, 'CANCELED');
                                  toast.success(`Unmerged group "${group.name}" successfully`);
                                } else {
                                  // Local merge group - just remove from state
                                  //console.log('🗑️ Removing local merge group:', group.id);
                                  toast.success('Local merge group removed');
                                }
                                
                                // Remove from local state using context method
                                removeMergeGroup(group.id);
                                
                                // Refresh today's merge groups to sync with backend
                                const today = new Date().toISOString().split('T')[0];
                                // Safely extract floorId from currentFloor (e.g., "floor2" -> 2)
                                let floorId = 1; // default to floor 1
                                if (currentFloor && typeof currentFloor === 'string') {
                                  const match = currentFloor.match(/floor(\d+)/);
                                  floorId = match ? parseInt(match[1]) : 1;
                                }
                                await refreshMergeGroups(today, floorId);
                                
                                // Also refresh floor data to update table statuses (from Reserved to Available)
                                if (currentFloor) {
                                  await loadIndividualFloorData(currentFloor);
                                }
                                
                              } catch (error) {
                                //console.error('❌ Failed to delete merge group:', error);
                                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                                toast.error(`Failed to delete merge group: ${errorMessage}`);
                              }
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        
                        {/* Group Details */}
                        <div className="space-yposition-1 text-xs text-gray-600">
                          {/* Customer Name - get from merged tables */}
                          {group.restaurantTableId && group.restaurantTableId.length > 0 && (
                            <div className="flex items-center gap-2">
                              <User className="w-3 h-3" />
                              <span className="font-medium">Customer:</span>
                              <span>
                                {(() => {
                                  // Find the first table with customer information
                                  const tableWithCustomer = currentFloorPlan.tables.find(table => 
                                    group.restaurantTableId?.some(groupTable => groupTable.id?.toString() === table.id) && 
                                    table.customer
                                  );
                                  return tableWithCustomer?.customer?.name || group.name.replace(' Party', '').replace('Party', '').trim();
                                })()}
                              </span>
                            </div>
                          )}
                          
                          {/* Booking Date */}
                          {'createdAt' in group && (group as { createdAt?: string }).createdAt && (
                            <div className="flex items-center gap-2">
                              <Clock className="w-3 h-3" />
                              <span className="font-medium">Booked Date:</span>
                              <span>{new Date((group as { createdAt?: string }).createdAt as string).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}</span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3 h-3" />
                            <span className="font-medium">Tables:</span>
                            <span>{group.restaurantTableId?.map(t => t.name).join(', ') || group.tableIds.join(', ')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="w-3 h-3" />
                            <span className="font-medium">Total Seats:</span>
                            <span>{group.totalCapacity} seats</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Circle className="w-3 h-3" />
                            <span className="font-medium">Tables Count:</span>
                            <span>{group.tableIds.length} tables merged</span>
                          </div>
                          {group.color && (
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded border border-gray-300" 
                                style={{ backgroundColor: group.color }}
                              />
                              <span className="font-medium">Color:</span>
                              <span>{group.color}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-yposition-3">
                <Button 
                  onClick={addNewTable}
                  variant="outline"
                  className="w-full justify-start bg-blue-100 hover:bg-blue-200"
                  size="sm"
                >
                  <PackagePlus className="w-4 h-4 mr-2" />
                  Add Table
                </Button>
                <Button 
                  onClick={addNewArea}
                  variant="outline"
                  className="w-full justify-start mt-2"
                  size="sm"
                >
                  Add Area
                </Button>
                <Button 
                  onClick={addNewDivider}
                  variant="outline"
                  className="w-full justify-start mt-2"
                  size="sm"
                >
                  Add Divider
                </Button>
                
                {selectedElement && (
                  <>
                    <Button 
                      onClick={deleteSelectedElement}
                      variant="red"
                      className="w-full justify-start mt-4"
                      size="sm"
                    >
                      <Trash2 className="w-4 h-4 mr-2 text-red-600" />
                      Delete Selected
                    </Button>
                    
                    {/* Divider Thickness Control */}
                    {selectedElementType === 'divider' && (() => {
                      const divider = currentFloorPlan.dividers.find(d => d.id === selectedElement);
                      return divider ? (
                        <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-md space-yposition-2">
                          <label className="text-xs font-semibold text-purple-900">
                            Thickness: {divider.thickness}px
                          </label>
                          <input
                            type="range"
                            min="1"
                            max="20"
                            value={divider.thickness}
                            onChange={(e) => {
                              const newThickness = parseInt(e.target.value);
                              setFloorPlans(prev => ({
                                ...prev,
                                [currentFloor]: {
                                  ...prev[currentFloor],
                                  dividers: prev[currentFloor].dividers.map(d =>
                                    d.id === selectedElement
                                      ? { ...d, thickness: newThickness }
                                      : d
                                  )
                                }
                              }));
                              setUnsavedChanges(true);
                              toast.success(`Thickness updated to ${newThickness}px`);
                            }}
                            className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                          />
                          <p className="text-xs text-gray-600">Drag to adjust wall thickness</p>
                        </div>
                      ) : null;
                    })()}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Column 2: Floor Plan Canvas */}
        <div className="flex-1 p-1 md:p-2 lg:p-3 min-h-80 lg:min-h-0 overflow-auto min-w-0">
          <Card className="h-full shadow-lg border-0 bg-white dark:bg-gray-900 flex flex-col">
            <CardHeader className="pb-2 pt-2 md:pb-1 dark:border-gray-700 flex-shrink-0">
              <CardTitle className="flex items-center justify-between">
                <span className="text-sm md:text-base lg:text-lg text-gray-800 dark:text-gray-100">{currentFloorPlan.name}</span>
                
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 p-0 overflow-hidden">
              <div className="relative w-full h-full bg-gray-200 dark:bg-gray-800 rounded-lg overflow-auto canvas-container">
                {/* Pan Controls Hint */}
                <div className="absolute bottom-2 left-4 z-10 bg-white dark:bg-gray-700 rounded-lg shadow-lg p-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-700 dark:text-gray-200">Pan:</span>
                    <span className="text-gray-600 dark:text-gray-300">Hold Shift + Drag or Middle Mouse</span>
                    {(canvasPan.x !== 0 || canvasPan.y !== 0) && (
                      <button
                        onClick={() => setCanvasPan({ x: 0, y: 0 })}
                        className="ml-2 px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                        title="Reset Pan"
                      >
                        Reset View
                      </button>
                    )}
                  </div>
                </div>

                <canvas
                  ref={canvasRef}
                  width={canvasDimensions.width}
                  height={canvasDimensions.height}
                  className="cursor-pointer bg-orange-50"
                  onClick={handleCanvasClick}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                />
              </div>
            </CardContent>
          </Card>
        </div>

  {/* Column 3: Table Details and Overview */}
  <div className="w-full lg:w-56 xl:w-64 bg-white dark:bg-gray-900 border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 flex flex-col max-h-48 lg:max-h-none overflow-y-auto lg:overflow-y-auto min-w-0">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Table Details</h3>
          </div>
          <div className="flex-1 p-4 overflow-y-auto">
            {showTableDetails && viewMode === 'static' ? (
              <div className="space-yposition-4">
                {(() => {
                  const table = getSelectedTable();
                  if (!table) return null;
                  
                  // Get booking details for this table
                  const booking = tableBookingDetails[table.id];
                  
                  return (
                    <>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold">{table.name}</h4>
                          <Badge variant={getStatusBadgeVariant(table.status)}>
                            {table.status}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-600 space-yposition-1">
                          <p>Seats: {table.seats} seats</p>
                          <p>Shape: {table.shape}</p>
                          <p>Last Updated: {table.lastUpdated.toLocaleTimeString()}</p>
                        </div>
                      </div>

                      {/* Booking Details */}
                      {booking && (
                        <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="font-medium text-blue-900 flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              Today's Booking
                            </h5>
                            
                          </div>
                          
                          <div className="text-sm space-yposition-2">
                            {booking.bookedId && (
                              <div className="flex items-center gap-2 mb-2 p-1 bg-white rounded">
                                <span className="font-medium text-blue-800">Booking ID:</span>
                                <span className="font-mono text-xs">{booking.bookedId}</span>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2">
                              <User className="w-3 h-3 text-blue-600" />
                              <span className="font-medium">Customer:</span>
                              <span className="font-semibold">{booking.username}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Mail className="w-3 h-3 text-blue-600" />
                              <span className="font-medium">Email:</span>
                              <span className="text-xs break-all">{booking.email}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="w-3 h-3 text-blue-600" />
                              <span className="font-medium">Phone:</span>
                              <span>{booking.phoneNo}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-3 h-3 text-blue-600" />
                              <span className="font-medium">Guests:</span>
                              <span className="font-semibold text-green-700">{booking.guestNo} people</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-3 h-3 text-blue-600" />
                              <span className="font-medium">Time:</span>
                              <span className="font-semibold">{booking.time}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-3 h-3 text-blue-600" />
                              <span className="font-medium">Date:</span>
                              <span className="font-semibold">{new Date(booking.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={booking.status === 'Accepted' ? 'default' : booking.status === 'Pending' ? 'secondary' : 'destructive'}>
                                {booking.status}
                              </Badge>
                            </div>
                            
                            {booking.message && (
                              <div className="mt-3 p-2 bg-white rounded text-xs border border-blue-200">
                                <strong className="text-blue-800">Special Requests:</strong>
                                <div className="mt-1 text-gray-700">{booking.message}</div>
                              </div>
                            )}
                            
                            {booking.restaurantTableId && booking.restaurantTableId.length > 1 && (
                              <div className="mt-3 p-2 bg-yellow-50 rounded text-xs border border-yellow-200">
                                <strong className="text-yellow-800">🔗 Merged Tables:</strong>
                                <div className="mt-1 text-yellow-700">
                                  {booking.restaurantTableId.map(t => t.name).join(', ')}
                                  <span className="ml-2 text-xs">({booking.restaurantTableId.length} tables combined)</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Show message if no booking found */}
                      {!booking && table.id in tableBookingDetails && (
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                          <p className="text-sm text-gray-600 text-center">
                            No booking for today
                          </p>
                        </div>
                      )}

                      {/* Legacy Customer Info (if available) */}
                      {table.customer && !booking && (
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <h5 className="font-medium text-blue-900 mb-2">Customer Info</h5>
                          <div className="text-sm space-yposition-1">
                            <div className="flex items-center gap-2">
                              <User className="w-3 h-3" />
                              <span>{table.customer.name}</span>
                            </div>
                            {table.customer.email && (
                              <div className="flex items-center gap-2">
                                <Mail className="w-3 h-3" />
                                <span className="text-xs">{table.customer.email}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <Phone className="w-3 h-3" />
                              <span>{table.customer.phone}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-3 h-3" />
                              <span>{table.customer.guestNo} guests</span>
                            </div>
                            {table.customer.reservationTime && (
                              <div className="flex items-center gap-2">
                                <Clock className="w-3 h-3" />
                                <span>{table.customer.reservationTime}</span>
                              </div>
                            )}
                            {table.customer.specialRequests && (
                              <div className="mt-2 text-xs text-gray-600">
                                <strong>Special Requests:</strong> {table.customer.specialRequests}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Table Reservations Section */}
                      {/* {(() => {
                        const reservations = tableReservations[table.id] || [];
                        return (
                          <div className="bg-purple-50 p-3 rounded-lg border-l-4 border-purple-500">
                            <h5 className="font-medium text-purple-900 mb-3 flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              Today's Reservations ({reservations.length})
                            </h5>
                            
                            {reservations.length === 0 ? (
                              <div className="text-sm text-purple-600 text-center py-2">
                                No reservations for this table today
                              </div>
                            ) : (
                              <div className="space-y-2 max-h-32 overflow-y-auto">
                                {reservations.map((reservation) => (
                                  <div
                                    key={reservation.id || reservation.bookedId}
                                    className="p-2 bg-white rounded border border-purple-200 text-sm"
                                  >
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-medium text-purple-900">
                                        {reservation.username}
                                      </span>
                                      <Badge className={`text-xs ${
                                        reservation.status === 'Accepted' ? 'bg-green-100 text-green-800' :
                                        reservation.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {reservation.status}
                                      </Badge>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-1 text-xs text-purple-700">
                                      <div className="flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        {reservation.time}
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Users className="h-3 w-3" />
                                        {reservation.guestNo} guests
                                      </div>
                                    </div>
                                    
                                    {reservation.message && (
                                      <div className="mt-1 text-xs text-purple-600 bg-purple-50 p-1 rounded">
                                        <strong>Note:</strong> {reservation.message}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()} */}

                      <div className="space-yposition-2">
                        <h5 className="font-medium text-gray-900">Change Status</h5>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {(['available', 'occupied', 'reserved', 'maintenance'] as const).map((status) => (
                            <Button
                              key={status}
                              size="sm"
                              variant={table.status === status ? 'blueBg' : 'outline'}
                              onClick={() => changeTableStatus(table.id, status)}
                              className="text-xs"
                            >
                              {status}
                            </Button>
                          ))}
                        </div>
                      </div>

                      <Button
                        variant="red"
                        onClick={() => setShowTableDetails(null)}
                        className="w-full mt-2 "
                        size="sm"
                      >
                        Close Details
                      </Button>
                    </>
                  );
                })()}
              </div>
            ) : (
              <div>
              </div>
            )}

            {/* Table Details & Overview */}
            
            {viewMode === 'static' && (
              <div className="mt-6 flex flex-col">
              <p className="font-medium text-blue-900 bg-blue-100 flex items-center gap-2">
            {showTableDetails ? "Reservations for this table" : "Today's reservations"}
          </p>
              <UpcomingReservationsCard 
                onTableClick={(tableId) => {
                  setShowTableDetails(tableId);
                  fetchTableBookingDetails(tableId);
                  fetchTableReservations(tableId);
                }}
                selectedFloor={currentFloor}
                selectedTableId={showTableDetails}
              />
              </div>
            )}

            {/* Edit Mode Info */}
            {viewMode === 'edit' && (
              <div className="space-y-4">
                {selectedElement ? (
                  <>
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <h5 className="font-semibold text-blue-900 mb-1">Selected Element</h5>
                      <p className="text-sm text-blue-700 capitalize">{selectedElementType}</p>
                    </div>

                    {/* Table Properties */}
                    {selectedElementType === 'table' && (() => {
                      const table = currentFloorPlan.tables.find(t => t.id === selectedElement);
                      return table ? (
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs font-semibold text-gray-700">Table Name</label>
                            <input
                              type="text"
                              value={table.name}
                              onChange={(e) => {
                                setFloorPlans(prev => ({
                                  ...prev,
                                  [currentFloor]: {
                                    ...prev[currentFloor],
                                    tables: prev[currentFloor].tables.map(t =>
                                      t.id === selectedElement ? { ...t, name: e.target.value } : t
                                    )
                                  }
                                }));
                                setUnsavedChanges(true);
                              }}
                              className="w-full mt-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-700">Seats</label>
                            <input
                              type="number"
                              min="1"
                              max="20"
                              value={table.seats}
                              onChange={(e) => {
                                setFloorPlans(prev => ({
                                  ...prev,
                                  [currentFloor]: {
                                    ...prev[currentFloor],
                                    tables: prev[currentFloor].tables.map(t =>
                                      t.id === selectedElement ? { ...t, seats: parseInt(e.target.value) || 1 } : t
                                    )
                                  }
                                }));
                                setUnsavedChanges(true);
                              }}
                              className="w-full mt-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-700">Shape</label>
                            <select
                              value={table.shape}
                              onChange={(e) => {
                                const newShape = e.target.value as 'rectangle' | 'circle';
                                //console.log(`🔄 Changing table shape to: ${newShape} for table: ${selectedElement}`);
                                setFloorPlans(prev => ({
                                  ...prev,
                                  [currentFloor]: {
                                    ...prev[currentFloor],
                                    tables: prev[currentFloor].tables.map(t =>
                                      t.id === selectedElement ? { 
                                        ...t, 
                                        shape: newShape,
                                        // Auto-update description when shape changes
                                        description: `${newShape.toLowerCase()} table`
                                      } : t
                                    )
                                  }
                                }));
                                setUnsavedChanges(true);
                              }}
                              className="w-full mt-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="rectangle">Rectangle</option>
                              <option value="circle">Circle</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Description: {table.description}</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-700">Width: {table.width}px</label>
                            <input
                              type="range"
                              min="50"
                              max="200"
                              value={table.width}
                              onChange={(e) => {
                                setFloorPlans(prev => ({
                                  ...prev,
                                  [currentFloor]: {
                                    ...prev[currentFloor],
                                    tables: prev[currentFloor].tables.map(t =>
                                      t.id === selectedElement ? { ...t, width: parseInt(e.target.value) } : t
                                    )
                                  }
                                }));
                                setUnsavedChanges(true);
                              }}
                              className="w-full mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-700">Height: {table.height}px</label>
                            <input
                              type="range"
                              min="50"
                              max="200"
                              value={table.height}
                              onChange={(e) => {
                                setFloorPlans(prev => ({
                                  ...prev,
                                  [currentFloor]: {
                                    ...prev[currentFloor],
                                    tables: prev[currentFloor].tables.map(t =>
                                      t.id === selectedElement ? { ...t, height: parseInt(e.target.value) } : t
                                    )
                                  }
                                }));
                                setUnsavedChanges(true);
                              }}
                              className="w-full mt-1"
                            />
                          </div>
                          {/* <div>
                            <label className="text-xs font-semibold text-gray-700">
                              Text Size: {((table.textSize || 1.0) * 100).toFixed(0)}%
                            </label>
                            <input
                              type="range"
                              min="50"
                              max="200"
                              step="10"
                              value={(table.textSize || 1.0) * 100}
                              onChange={(e) => {
                                const newTextSize = parseInt(e.target.value) / 100;
                                setFloorPlans(prev => ({
                                  ...prev,
                                  [currentFloor]: {
                                    ...prev[currentFloor],
                                    tables: prev[currentFloor].tables.map(t => {
                                      if (t.id === selectedElement) {
                                        // Save to localStorage (UI-only, not saved to backend)
                                        setTextSizeToLocalStorage(t.id, newTextSize);
                                        return { ...t, textSize: newTextSize };
                                      }
                                      return t;
                                    })
                                  }
                                }));
                                // Note: textSize is UI-only, doesn't trigger unsaved changes
                              }}
                              className="w-full mt-1"
                            />
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                              <span>50%</span>
                              <span>100%</span>
                              <span>200%</span>
                            </div>
                          </div> */}
                        </div>
                      ) : null;
                    })()}

                    {/* Area Properties */}
                    {selectedElementType === 'area' && (() => {
                      const area = currentFloorPlan.areas.find(a => a.id === selectedElement);
                      return area ? (
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs font-semibold text-gray-700">Area Name</label>
                            <input
                              type="text"
                              value={area.name}
                              onChange={(e) => {
                                setFloorPlans(prev => ({
                                  ...prev,
                                  [currentFloor]: {
                                    ...prev[currentFloor],
                                    areas: prev[currentFloor].areas.map(a =>
                                      a.id === selectedElement ? { ...a, name: e.target.value } : a
                                    )
                                  }
                                }));
                                setUnsavedChanges(true);
                              }}
                              className="w-full mt-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-700">Color</label>
                            <input
                              type="color"
                              value={area.color}
                              onChange={(e) => {
                                setFloorPlans(prev => ({
                                  ...prev,
                                  [currentFloor]: {
                                    ...prev[currentFloor],
                                    areas: prev[currentFloor].areas.map(a =>
                                      a.id === selectedElement ? { ...a, color: e.target.value } : a
                                    )
                                  }
                                }));
                                setUnsavedChanges(true);
                              }}
                              className="w-full mt-1 h-10 border border-gray-300 rounded cursor-pointer"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-700">Width: {area.width}px</label>
                            <input
                              type="range"
                              min="100"
                              max="500"
                              value={area.width}
                              onChange={(e) => {
                                setFloorPlans(prev => ({
                                  ...prev,
                                  [currentFloor]: {
                                    ...prev[currentFloor],
                                    areas: prev[currentFloor].areas.map(a =>
                                      a.id === selectedElement ? { ...a, width: parseInt(e.target.value) } : a
                                    )
                                  }
                                }));
                                setUnsavedChanges(true);
                              }}
                              className="w-full mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-700">Height: {area.height}px</label>
                            <input
                              type="range"
                              min="100"
                              max="500"
                              value={area.height}
                              onChange={(e) => {
                                setFloorPlans(prev => ({
                                  ...prev,
                                  [currentFloor]: {
                                    ...prev[currentFloor],
                                    areas: prev[currentFloor].areas.map(a =>
                                      a.id === selectedElement ? { ...a, height: parseInt(e.target.value) } : a
                                    )
                                  }
                                }));
                                setUnsavedChanges(true);
                              }}
                              className="w-full mt-1"
                            />
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {/* Divider Properties - already exists in left panel */}
                    {selectedElementType === 'divider' && (
                      <div className="text-sm text-gray-600">
                        <p>Use the thickness slider in the left panel to adjust divider properties.</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center text-gray-500 mt-8">
                    <Settings className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Select an element to edit its properties</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Merge Preview Dialog */}
      <MergePreviewDialog
        open={showMergePreview}
        onOpenChange={setShowMergePreview}
        selectedTables={currentFloorPlan.tables
          .filter(t => selectedTablesForMerge.includes(t.id))
          .map(t => ({
            id: t.id,
            name: t.name,
            seats: t.seats,
            status: t.status
          }))
        }
        onConfirm={handleMergeConfirm}
        onCancel={handleMergeCancel}
      />

      {/* Table Delete Confirmation Dialog */}
      <TableDeleteConfirmationDialog
        open={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}
        tableName={deleteTableInfo?.name || ''}
        hasReservations={deleteTableInfo?.hasReservations || false}
        reservationCount={deleteTableInfo?.reservationCount || 0}
        reservationDetails={deleteTableInfo?.reservationDetails || []}
        onConfirmDelete={async () => {
          setShowDeleteConfirmation(false);
          await performElementDeletion();
        }}
        onCancel={() => {
          setShowDeleteConfirmation(false);
          setDeleteTableInfo(null);
        }}
      />

      {/* Merge History Dialog */}
      <MergeHistoryDialog
        open={showMergeHistory}
        onOpenChange={setShowMergeHistory}
        onMergeGroupsUpdated={() => {
          // Refresh today's merge groups after unmerge with floorId
          const today = new Date().toISOString().split('T')[0];
          // Safely extract floorId from currentFloor (e.g., "floor2" -> 2)
          let floorId = 1;
          if (currentFloor && typeof currentFloor === 'string') {
            const match = currentFloor.match(/floor(\d+)/);
            floorId = match ? parseInt(match[1]) : 1;
          }
          refreshMergeGroups(today, floorId);
        }}
      />
    </div>
  );
};

export default AdvancedFloorPlanEditor;